/* nutrient-ops theme-bundle: help-me-choose v20 — installed by the LP Builder from nutrient-ops (website/help-me-choose/theme). Edit the repo copy and reinstall; hand edits here are overwritten on the next install. */
/* Nutrient Survival — Help Me Choose planner
 * Theme-ready: NSHMC.init({ root, catalog, config }) renders into `root`.
 * The theme section supplies `catalog` (from the NFP metaobjects) and `config`;
 * the prototype supplies the same shapes inline. No dependencies.
 *
 * State lives in the URL hash (no server): p adults, k kids, d days, c calorie
 * preset, r restrictions, x removed handles, q quantity overrides, s=0 cans-only,
 * sx staples left out, b=0 basics off, v=plan presentation view.
 */
window.NSHMC = (function () {
  "use strict";

  /* ---------- defaults (the theme section can override any of these) ---------- */
  const DEFAULTS = {
    slotOf: {
      "homestyle-scramble-protein-meal-10-can": "breakfast",
      "protein-cereal-chocolate-10-can": "breakfast",
      "protein-cereal-maple-almond-10-can": "breakfast",
      "protein-oatmeal-apple-cinnamon-10-can": "breakfast",
      "hearty-lasagna-protein-meal-10-can": "entree",
      "triple-cheese-mac-protein-meal-10-can": "entree",
      "southwestern-medley-protein-meal-10-can": "entree",
      "powdered-vitamin-milk-10-can": "milk",
      "freeze-dried-strawberry-slices-10-can": "fruitveg",
      "freeze-dried-mixed-vegetables-10-can": "fruitveg",
      "freeze-dried-banana-slices-10-can": "fruitveg",
      "freeze-dried-green-peas-10-can": "fruitveg",
      "freeze-dried-kernel-corn-10-can": "fruitveg",
      "freeze-dried-apple-slices-10-can": "fruitveg",
      "freeze-dried-blueberries-10-can": "fruitveg",
      "freeze-dried-mango-chunks-10-can": "fruitveg",
      "freeze-dried-raspberry-pieces-10-can": "fruitveg",
      "powdered-vitamin-eggs-10-can": "basics",
      "powdered-vitamin-butter-10-can": "basics",
      "instant-vitamin-potato-10-can": "basics",
      "powdered-vitamin-tomato-10-can": "basics",
      "whey-protein-powder-chocolate-10-can": "extras",
      "whey-protein-powder-vanilla-10-can": "extras",
    },
    slots: [
      { key: "breakfast", label: "Breakfast", perDay: 1, why: "One breakfast a day." },
      { key: "entree", label: "Lunch and dinner", perDay: 2, why: "Two meals a day." },
      { key: "milk", label: "Milk", perDay: 2, why: "Two cups a day, for drinking and cereal." },
      { key: "fruitveg", label: "Fruit and vegetables", perDay: 1, why: "One serving a day." },
    ],
    // Kitchen basics are rate-based extras: servings per person per day.
    basics: {
      "powdered-vitamin-eggs-10-can": { rate: 0.5, why: "An egg serving every other day." },
      "powdered-vitamin-butter-10-can": { rate: 2, why: "Two pats a day for rice, potatoes, and pasta." },
      "instant-vitamin-potato-10-can": { rate: 0.5, why: "A potato side every other day." },
      "powdered-vitamin-tomato-10-can": { rate: 0.33, why: "Pasta sauce every third day." },
    },
    // Extras are never recommended; they can be added from the group's "Add" menu.
    extrasLabel: "Extras",
    extrasWhy: "Protein shakes and smoothies. Optional.",
    tiers: [
      [24, 0.24],
      [18, 0.18],
      [12, 0.12],
      [6, 0.06],
    ],
    calories: [
      { key: "lighter", label: "Lighter", kcal: 1600, hint: "Short stays, lighter appetites" },
      { key: "standard", label: "Standard", kcal: 2000, hint: "Most adults" },
      { key: "active", label: "Active", kcal: 2400, hint: "Hard work, cold weather" },
    ],
    dayPresets: [3, 14, 30, 90, 180, 365],
    kidFoodFactor: 0.5,  // a child counts as half an adult for food
    kidWaterFactor: 1,   // and as a full person for drinking water
    maxAdults: 12,
    maxKids: 12,
    largeGroup: 12,      // adults + kids above this shows the "talk to us" notice
    // proteinLb = grams of protein per dry pound; gPerCupDry / cookedPerDryCup turn a
    // daily gram amount into kitchen measures for the "what a day looks like" card.
    staples: [
      { key: "rice", label: "White rice", kcalLb: 1650, proteinLb: 32, gPerCupDry: 185, cookedPerDryCup: 3, waterCupsLb: 4.6, share: 0.5, costLb: 0.8, shelf: "25+ years sealed with oxygen absorbers", cook: "2 cups water per cup dry, 20 minutes" },
      { key: "pasta", label: "Pasta", kcalLb: 1680, proteinLb: 59, gPerCupDry: 100, cookedPerDryCup: 2, waterCupsLb: 8, share: 0.3, costLb: 1.3, shelf: "8 to 10 years sealed", cook: "Reuse the water between batches" },
      { key: "beans", label: "Dry beans", kcalLb: 1570, proteinLb: 95, gPerCupDry: 190, cookedPerDryCup: 3, waterCupsLb: 9, share: 0.2, costLb: 1.5, shelf: "Safe for decades, harder to cook after 2 to 3 years", cook: "Soak, then simmer 1 to 2 hours" },
    ],
    // What the page calls the rice, pasta and beans layer. One place, so renaming it is one edit.
    words: {
      title: "Rice, pasta & beans",  // card title, summary line
      lower: "rice, pasta & beans",  // mid-sentence, lunch-and-dinner option
      short: "rice, pasta & beans",  // legend, water line, short mentions
      list: "Grocery list",          // plan view list heading
      listLower: "grocery list",     // mid-sentence
    },
    staplesWhy: [
      "Our cans carry the nutrition: every serving is built around all 40 essential nutrients. Calories are the cheap part of a day, and rice, pasta, and beans deliver them for about a dollar a pound.",
      "So each lunch and dinner is one can serving for the nutrition plus a portion of rice, pasta, or beans for the fuel. A 2,000-calorie day costs a fraction of what it would from cans alone, and the food is more like what you cook now.",
      "You buy the rice, pasta, and beans anywhere, store them sealed with oxygen absorbers, and the plan tells you how many pounds for the whole period. Prefer not to cook them? Switch to two can servings per meal under Lunch and dinner.",
    ],
    pairings: [
      { with: ["powdered-vitamin-tomato-10-can", "freeze-dried-mixed-vegetables-10-can"], text: "Tomato powder and mixed vegetables make a pasta sauce." },
      { with: ["powdered-vitamin-milk-10-can"], anyOf: ["protein-cereal-chocolate-10-can", "protein-cereal-maple-almond-10-can", "protein-oatmeal-apple-cinnamon-10-can"], text: "Milk goes on cereal and into oatmeal. Add fruit on top." },
      { with: ["powdered-vitamin-butter-10-can"], text: "Butter finishes rice, potatoes, and pasta." },
      { with: ["powdered-vitamin-eggs-10-can", "powdered-vitamin-butter-10-can"], text: "Eggs scrambled in butter with a handful of vegetables is a real breakfast." },
      { with: ["powdered-vitamin-eggs-10-can", "instant-vitamin-potato-10-can"], text: "Eggs and potatoes make a second breakfast or a quick dinner." },
      { with: ["instant-vitamin-potato-10-can", "freeze-dried-mixed-vegetables-10-can"], text: "Stir vegetables into the potatoes." },
      { with: ["whey-protein-powder-chocolate-10-can"], anyOf: ["freeze-dried-strawberry-slices-10-can", "freeze-dried-banana-slices-10-can", "freeze-dried-blueberries-10-can", "freeze-dried-mango-chunks-10-can", "freeze-dried-raspberry-pieces-10-can"], text: "Fruit and protein powder blend into a smoothie." },
      { with: ["whey-protein-powder-vanilla-10-can"], anyOf: ["freeze-dried-strawberry-slices-10-can", "freeze-dried-banana-slices-10-can", "freeze-dried-blueberries-10-can", "freeze-dried-mango-chunks-10-can", "freeze-dried-raspberry-pieces-10-can"], text: "Fruit and protein powder blend into a smoothie." },
    ],
    meatNote: "Have meat on hand, fresh, freeze-dried, or dehydrated? Add it to any entree, the eggs, potatoes, tomato sauce, or vegetables for extra protein and calories.",
    drinkingGalPerPersonDay: 1,
    containers: [
      { key: "jug", label: "5-gallon jug", gal: 5, cost: "$15 to $30 each", note: "About 42 lb full. The easiest to carry, fill, and rotate. Stack them on a shelf, not on concrete." },
      { key: "barrel", label: "55-gallon barrel", gal: 55, cost: "$70 to $150 new, $30 to $60 used", note: "Food-grade HDPE only, blue is the usual color. About 23 in. across, 35 in. tall, and 480 lb full, so fill it where it will live. You will want a hand pump and a bung wrench." },
      { key: "ibc", label: "275-gallon IBC tote", gal: 275, cost: "$200 to $400 new, $80 to $180 reconditioned", note: "48 by 40 in. footprint, 46 in. tall, about 2,400 lb full. Only a tote that held food or nothing before, never chemicals. Needs a solid, level floor and shade, since sunlight grows algae. A garage or basement is ideal. A 330-gallon version exists for a bit more." },
    ],
    restrictions: [
      { key: "gluten", label: "Gluten", test: (c) => c.gf && !c.allergens.includes("wheat") },
      { key: "dairy", label: "Dairy", test: (c) => !c.allergens.includes("milk") },
      { key: "eggs", label: "Eggs", test: (c) => !c.allergens.includes("eggs") },
      { key: "soy", label: "Soy", test: (c) => !c.allergens.includes("soy") },
      { key: "nuts", label: "Tree nuts", test: (c) => !c.allergens.some((a) => /almond|nut|cashew|pecan|walnut|pistachio|hazelnut/.test(a)) },
      { key: "vegan", label: "Animal products", test: (c) => c.vegan },
    ],
    // Words in a kit's contents lines that mean a product word (kit copy uses marketing names).
    kitAliases: { shake: "whey", crunch: "cereal", grain: "cereal", hearty: "", vitamin: "", powdered: "", instant: "", protein: "", meal: "", can: "", cans: "", "#10": "" },
    // "New here?" section: built-in copy unless the section setting supplies HTML.
    educationHtml: "",
    educationLinks: { about: "/pages/about-us", nutrition: "/pages/nutrition", reviews: "/pages/reviews", opsGrade: "/pages/special-ops-grade" },
    freeShipping: 149,
    images: true,
    cartPath: "/cart",
    varietyServingsPerProduct: 25,
    stickyOffset: 0,
    klaviyo: { companyId: "", listId: "", metric: "Help Me Choose Plan Saved", revision: "2024-10-15" },
    labels: {
      inputs: "Who are you feeding?",
      plan: "Your food",
      staples: "Rice, pasta & beans",
      water: "Water to store",
      tips: "Storage and cooking tips",
      kit: "Or start from a kit",
      summary: "Your plan",
      cart: "Add {n} cans to cart",
      email: "Email me this plan",
      copy: "Copy link",
      view: "See my plan",
      payNote: "Pay in full, or use Shop Pay Installments at checkout.",
      emailIntro: "We'll email you this exact plan: every can, the grocery list, and the water numbers. Nothing else unless you ask.",
      emailOptIn: "Also send me prep tips and offers now and then.",
      emailSent: "Sent. Give it a few minutes and check your inbox.",
      emailError: "That didn't go through. Check the address and try again, or copy the link instead.",
      largeGroup: "Feeding a group this size? Call or chat with us and we'll build it with you, with pallet pricing where it applies.",
      education: "New here? Start with this",
      day: "What a day looks like",
    },
  };

  const CUP = { Cup: 1, Cups: 1, Tablespoon: 1 / 16, Teaspoon: 1 / 48, Ounce: 1 / 8 };

  /* ---------- state ---------- */
  let cfg, catalog, root, state;

  const defaultState = () => ({
    adults: 1,
    kids: 0,
    days: 90,
    kcal: "standard",
    restrictions: [],
    removed: [],
    qty: {},          // handle → cans (user override; 0 never stored, that is `removed`)
    staples: true,    // true = 1 entree serving/meal + staples; false = 2 servings, cans only
    staplesRemoved: [],
    basics: true,
    kit: null,        // handle of the kit the plan was started from (limits products to the kit's)
    autoCans: false,  // session only: cans-only was switched on because every staple was removed
    topUp: {},        // session only: handle → its quantity override before "Top up with cans" (null = none)
    view: "build",    // "build" | "plan"
  });
  const MAX_CANS = 999;
  const knownHandle = (hd) => catalog.cans.some((c) => c.handle === hd);
  const uniq = (list) => [...new Set(list)];

  function readHash() {
    const s = defaultState();
    if (!cfg.calories.some((c) => c.key === s.kcal)) s.kcal = cfg.calories[0].key;
    const h = location.hash.replace(/^#/, "");
    if (!h) return s;
    const q = new URLSearchParams(h);
    if (q.get("p")) s.adults = clamp(parseInt(q.get("p"), 10) || 1, 1, cfg.maxAdults);
    if (q.get("k")) s.kids = clamp(parseInt(q.get("k"), 10) || 0, 0, cfg.maxKids);
    if (q.get("d")) s.days = clamp(parseInt(q.get("d"), 10) || 90, 1, 365);
    if (q.get("c") && cfg.calories.some((c) => c.key === q.get("c"))) s.kcal = q.get("c");
    // Every list from the URL is validated against what this page knows: unknown restriction
    // keys, product handles, staple keys and kit handles are dropped (a shared link is untrusted input).
    if (q.get("r")) s.restrictions = uniq(q.get("r").split(",")).filter((k) => cfg.restrictions.some((x) => x.key === k));
    if (q.get("x")) s.removed = uniq(q.get("x").split(",")).filter(knownHandle);
    if (q.get("q")) q.get("q").split(",").forEach((pair) => { const [hd, n] = pair.split(":"); const v = parseInt(n, 10); if (hd && knownHandle(hd) && v > 0) s.qty[hd] = clamp(v, 1, MAX_CANS); });
    if (q.get("s") === "0") s.staples = false;
    if (q.get("sx")) s.staplesRemoved = uniq(q.get("sx").split(",")).filter((k) => cfg.staples.some((x) => x.key === k));
    if (q.get("b") === "0") s.basics = false;
    if (q.get("kit") && catalog.kits.some((k) => k.handle === q.get("kit"))) s.kit = q.get("kit");
    if (q.get("v") === "plan") s.view = "plan";
    return s;
  }
  // Does this hash carry any planner key at all? (A skip-link "#main" must not reset the plan.)
  function hashHasPlanKeys(hash) {
    const q = new URLSearchParams(String(hash || "").replace(/^#/, ""));
    return ["p", "k", "d", "c", "r", "x", "q", "s", "sx", "b", "kit", "v"].some((k) => q.has(k));
  }
  function hashFor(st) {
    const q = new URLSearchParams();
    q.set("p", st.adults);
    if (st.kids) q.set("k", st.kids);
    q.set("d", st.days);
    q.set("c", st.kcal);
    if (st.restrictions.length) q.set("r", st.restrictions.join(","));
    if (st.removed.length) q.set("x", st.removed.join(","));
    const qs = Object.entries(st.qty).filter(([, n]) => n > 0).map(([hd, n]) => `${hd}:${n}`);
    if (qs.length) q.set("q", qs.join(","));
    if (!st.staples) q.set("s", "0");
    if (st.staplesRemoved.length) q.set("sx", st.staplesRemoved.join(","));
    if (!st.basics) q.set("b", "0");
    if (st.kit) q.set("kit", st.kit);
    if (st.view === "plan") q.set("v", "plan");
    return "#" + q.toString();
  }
  function writeHash() { history.replaceState(null, "", hashFor(state)); }

  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));
  const money = (n) => "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const money0 = (n) => "$" + Math.round(n).toLocaleString("en-US");
  const num = (n) => Math.round(n).toLocaleString("en-US");
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const plural = (n, one, many) => n === 1 ? one : (many || one + "s");
  const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 100;

  /* ---------- planning ---------- */
  function displayName(c) {
    return c.title.replace(/\s*#10 Can\s*$/i, "").replace(" - Protein Meal", "");
  }
  function waterCups(c) {
    if (c.rte || !c.waterAmt) return 0;
    return c.waterAmt * (CUP[c.waterUnit] ?? 1);
  }
  function allowed(c) {
    return state.restrictions.every((k) => {
      const r = cfg.restrictions.find((x) => x.key === k);
      return !r || typeof r.test !== "function" || r.test(c);
    });
  }
  function foodPeople() { return state.adults + state.kids * cfg.kidFoodFactor; }
  function waterPeople() { return state.adults + state.kids * cfg.kidWaterFactor; }
  // A can's meal slot: the config map first, then the product's tags, so a new #10 can
  // shows up in the right group before anyone edits the config.
  function slotFor(c) {
    if (cfg.slotOf[c.handle]) return cfg.slotOf[c.handle];
    const tags = (c.tags || []).map((t) => String(t).toLowerCase());
    const has = (re) => tags.some((t) => re.test(t));
    if (has(/protein powder|protein shakes/)) return "extras";
    if (has(/^breakfasts?$/)) return "breakfast";
    if (has(/entree/)) return "entree";
    if (has(/fruit|vegetable/)) return "fruitveg";
    if (has(/kitchen basics|^basics$/)) return "basics";
    return null;
  }
  function slotPool(key) {
    const order = Object.keys(cfg.slotOf);
    const inKit = state.kit ? kitQuantities(catalog.kits.find((k) => k.handle === state.kit)) : null;
    return catalog.cans
      .filter((c) => slotFor(c) === key && c.available !== false && (!inKit || inKit[c.handle]))
      .sort((a, b) => { const ia = order.indexOf(a.handle), ib = order.indexOf(b.handle); return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib); }); // variety order = config order
  }

  // Spread N servings across up to v products, whole cans, minimal overage.
  function allocate(items, N) {
    if (!items.length || N <= 0) return [];
    const v = Math.min(items.length, Math.max(1, Math.ceil(N / cfg.varietyServingsPerProduct)));
    const chosen = items.slice(0, v);
    const target = N / v;
    const cans = chosen.map((c) => Math.floor(target / c.servings));
    let total = chosen.reduce((s, c, i) => s + cans[i] * c.servings, 0);
    let guard = 0;
    while (total < N && guard++ < 500) {
      let best = 0, bestRatio = Infinity;
      chosen.forEach((c, i) => {
        const ratio = (cans[i] * c.servings) / target;
        if (ratio < bestRatio) { bestRatio = ratio; best = i; }
      });
      cans[best] += 1;
      total += chosen[best].servings;
    }
    return chosen.map((c, i) => ({ c, cans: cans[i] })).filter((x) => x.cans > 0);
  }

  function compute() {
    const personDays = foodPeople() * state.days;
    const target = (cfg.calories.find((c) => c.key === state.kcal) || cfg.calories[0]).kcal;
    const entreeServingsPerMeal = state.staples ? 1 : 2;

    const lines = [];     // { slot, c, cans, recommended, servings, why }
    const groups = [];    // { key, label, why, needed, provided, coverage, gap, addable[] }
    const gaps = [];

    const lineFor = (slot, c, cans, recommended, extra) => {
      const l = Object.assign({ slot, c, cans, recommended, servings: cans * c.servings, edited: cans !== recommended }, extra || {});
      lines.push(l);
      return l;
    };

    cfg.slots.forEach((slot) => {
      const perDay = slot.key === "entree" ? slot.perDay * entreeServingsPerMeal : slot.perDay;
      const N = Math.ceil(personDays * perDay);
      const pool = slotPool(slot.key);
      const eligible = pool.filter((c) => allowed(c) && !state.removed.includes(c.handle));
      const alloc = allocate(eligible, N);
      const used = new Set();
      alloc.forEach((a) => {
        const q = state.qty[a.c.handle];
        used.add(a.c.handle);
        lineFor(slot.key, a.c, q != null ? q : a.cans, a.cans);
      });
      // products the customer added to this group by hand
      eligible.forEach((c) => { if (!used.has(c.handle) && state.qty[c.handle] > 0) { used.add(c.handle); lineFor(slot.key, c, state.qty[c.handle], 0); } });
      const provided = lines.filter((l) => l.slot === slot.key).reduce((s, l) => s + l.servings, 0);
      const addable = eligible.filter((c) => !used.has(c.handle));
      groups.push({ key: slot.key, label: slot.label, why: slot.why, needed: N, provided, coverage: pct(provided, N), addable, perDay });
      if (!alloc.length && !provided) {
        const blockedByDiet = pool.filter((c) => !allowed(c)).length;
        gaps.push({ slot, N, reason: !pool.length && state.kit ? "kit" : blockedByDiet === pool.length && pool.length ? "diet" : "removed" });
      }
    });

    // kitchen basics (rate-based) and extras (never recommended, only added)
    const basicsPool = slotPool("basics");
    const basicsLines = [];
    basicsPool.forEach((c) => {
      const b = cfg.basics[c.handle];
      if (!allowed(c) || state.removed.includes(c.handle)) return;
      const q = state.qty[c.handle];
      if (state.basics && b) {
        const N = Math.ceil(personDays * b.rate);
        const rec = Math.max(1, Math.ceil(N / c.servings));
        basicsLines.push(lineFor("basics", c, q != null ? q : rec, rec, { why: b.why }));
      } else if (q > 0) {
        basicsLines.push(lineFor("basics", c, q, 0, { why: b ? b.why : "" }));
      }
    });
    groups.push({ key: "basics", label: "Kitchen basics", why: "Small extras that turn rice, pasta, and potatoes into meals.", needed: 0, provided: basicsLines.reduce((s, l) => s + l.servings, 0), coverage: 100, addable: basicsPool.filter((c) => allowed(c) && !state.removed.includes(c.handle) && !basicsLines.some((l) => l.c.handle === c.handle)), perDay: 0 });

    const extrasPool = slotPool("extras");
    const extrasLines = [];
    extrasPool.forEach((c) => { if (allowed(c) && state.qty[c.handle] > 0) extrasLines.push(lineFor("extras", c, state.qty[c.handle], 0)); });
    groups.push({ key: "extras", label: cfg.extrasLabel, why: cfg.extrasWhy, needed: 0, provided: extrasLines.reduce((s, l) => s + l.servings, 0), coverage: 100, addable: extrasPool.filter((c) => allowed(c) && !state.removed.includes(c.handle) && !extrasLines.some((l) => l.c.handle === c.handle)), perDay: 0 });

    // Servings a person actually eats per day, per line. Whole-can rounding leaves leftovers,
    // not extra meals, so a recommended group counts at its intended daily amount unless the
    // customer raised a recommended quantity by hand (then the extra counts — "add calories by
    // adding cans"). Hand-added products count as bought without lifting the group's cap.
    // Kitchen basics count at their daily rate; extras (whey) count as bought.
    const eaten = new Map(); // line → servings per day
    cfg.slots.forEach((slot) => {
      const g = groups.find((x) => x.key === slot.key);
      const gl = lines.filter((l) => l.slot === slot.key);
      if (!gl.length || !g || personDays <= 0) return;
      const rec = gl.filter((l) => l.recommended > 0), added = gl.filter((l) => l.recommended === 0);
      const raised = rec.some((l) => l.edited && l.cans > l.recommended);
      const recActual = rec.reduce((s, l) => s + l.servings, 0) / personDays;
      const scale = raised || recActual <= g.perDay || recActual === 0 ? 1 : g.perDay / recActual;
      rec.forEach((l) => eaten.set(l, (l.servings / personDays) * scale));
      added.forEach((l) => eaten.set(l, l.servings / personDays));
    });
    lines.filter((l) => l.slot === "basics").forEach((l) => {
      const b = cfg.basics[l.c.handle];
      const actual = personDays > 0 ? l.servings / personDays : 0;
      eaten.set(l, b && !(l.edited && l.cans > l.recommended) ? Math.min(actual, b.rate) : actual);
    });
    lines.filter((l) => l.slot === "extras").forEach((l) => eaten.set(l, personDays > 0 ? l.servings / personDays : 0));
    const eatenPerDay = (fn) => lines.reduce((s, l) => s + (eaten.get(l) || 0) * fn(l.c), 0);
    const calPerDay = personDays > 0 ? eatenPerDay((c) => c.cal) : 0;

    const canCount = lines.reduce((s, l) => s + l.cans, 0);
    const list = lines.reduce((s, l) => s + l.cans * l.c.price, 0);
    const tier = cfg.tiers.find(([min]) => canCount >= min);
    const tierPct = tier ? tier[1] : 0;
    const nextTier = [...cfg.tiers].reverse().find(([min]) => canCount < min);
    const discount = list * tierPct;
    const total = list - discount;

    // staples
    const gapPerDay = Math.max(0, target - calPerDay);
    let staples = [];
    let staplesCost = 0, staplesWaterCups = 0, staplesKcalPerDay = 0;
    if (state.staples && gapPerDay > 0) {
      const active = cfg.staples.filter((s) => !state.staplesRemoved.includes(s.key));
      const shareSum = active.reduce((s, x) => s + x.share, 0) || 1;
      const kcalTotal = gapPerDay * personDays;
      staples = active.map((s) => {
        const kcal = kcalTotal * (s.share / shareSum);
        const lbs = Math.ceil(kcal / s.kcalLb);
        staplesCost += lbs * s.costLb;
        staplesWaterCups += lbs * s.waterCupsLb;
        staplesKcalPerDay += (lbs * s.kcalLb) / personDays;
        return { s, lbs };
      });
    }

    // Water follows what is eaten (like calories), not the whole cans bought.
    const cookCups = lines.reduce((s, l) => s + (eaten.get(l) || 0) * personDays * waterCups(l.c), 0);
    const cookGal = cookCups / 16;
    const stapleGal = staplesWaterCups / 16;
    const drinkGal = waterPeople() * state.days * cfg.drinkingGalPerPersonDay;
    const totalGal = cookGal + stapleGal + drinkGal;

    const hotItems = lines.filter((l) => l.c.hot).length;
    const kit = nearestKit();
    const edited = lines.some((l) => l.edited) || !!state.kit;

    const proteinPerDay = personDays > 0 ? eatenPerDay((c) => c.protein || 0) : 0;
    // Staples round up to whole pounds too; what gets eaten is the gap, not the bag.
    let staplesProteinPerDay = personDays > 0 ? staples.reduce((s, x) => s + (x.lbs * (x.s.proteinLb || 0)) / personDays, 0) : 0;
    let stapleScale = 1;
    if (staplesKcalPerDay > gapPerDay && staplesKcalPerDay > 0) {
      stapleScale = gapPerDay / staplesKcalPerDay;
      staplesProteinPerDay *= stapleScale;
      staplesKcalPerDay = gapPerDay;
    }
    const day = typicalDay(lines, staples, personDays, eaten, stapleScale);

    return {
      target, personDays, lines, groups, gaps, canCount, list, pct: tierPct, discount, total, nextTier,
      calPerDay, gapPerDay, staples, staplesCost, staplesKcalPerDay,
      totalCalPerDay: calPerDay + staplesKcalPerDay,
      proteinPerDay, staplesProteinPerDay, totalProteinPerDay: proteinPerDay + staplesProteinPerDay, day,
      cookGal, stapleGal, drinkGal, totalGal, hotItems, kit, edited,
      // "Per person per day" is a headcount figure: adults and kids each count as one.
      perPersonDay: state.days > 0 ? (total + staplesCost) / ((state.adults + state.kids) * state.days) : 0,
      servings: lines.reduce((s, l) => s + l.servings, 0),
      largeGroup: state.adults + state.kids > cfg.largeGroup,
    };
  }

  function nearestKit() {
    if (!catalog.kits || !catalog.kits.length) return null;
    const k = catalog.kits
      .filter((k) => k.days >= 14)
      .map((k) => ({ k, diff: Math.abs(k.days - state.days) / state.days }))
      .sort((a, b) => a.diff - b.diff)[0];
    if (!k || k.diff > 0.2) return null;
    return k.k;
  }

  // One adult's day on this plan, built from what is actually in it: the first
  // product in each meal group (variety rotates in real life), plus each staple's
  // daily share split across lunch and dinner.
  function typicalDay(lines, staples, personDays, eaten, stapleScale) {
    if (!lines.length || personDays <= 0) return null;
    const q4 = (n) => Math.round(n * 4) / 4;
    const perDay = (l) => (eaten && eaten.get(l)) || 0;
    const slotTotal = (slot) => lines.filter((l) => l.slot === slot).reduce((s, l) => s + perDay(l), 0);
    const first = (slot) => lines.find((l) => l.slot === slot);
    const part = (l, servings) => (l && servings >= 0.25) ? { text: `${fmtServings(q4(servings))} ${displayName(l.c)}`, cal: servings * l.c.cal, protein: servings * (l.c.protein || 0) } : null;
    const staplePart = (x, fraction) => {
      const gPerDay = (x.lbs * 453.6) / personDays * (stapleScale || 1) * fraction;
      if (gPerDay < 8) return null;
      const dryCups = gPerDay / x.s.gPerCupDry;
      return { text: `${fmtCups(dryCups * x.s.cookedPerDryCup)} cooked ${x.s.key === "beans" ? "beans" : x.s.label.toLowerCase()} (${fmtCups(dryCups)} dry)`, cal: gPerDay / 453.6 * x.s.kcalLb, protein: gPerDay / 453.6 * (x.s.proteinLb || 0) };
    };
    const meal = (title, parts) => { const ps = parts.filter(Boolean); return { title, parts: ps, cal: ps.reduce((s, p) => s + p.cal, 0), protein: ps.reduce((s, p) => s + p.protein, 0) }; };
    const entrees = lines.filter((l) => l.slot === "entree");
    const perMeal = slotTotal("entree") / 2;
    const milk = slotTotal("milk"), milkAtBreakfast = Math.min(1, milk);
    // Lunch gets the rice, dinner the pasta and beans: closer to how people cook than thirds of everything twice.
    const byKey = (k) => staples.find((x) => x.s.key === k);
    const others = staples.filter((x) => x.s.key !== "rice");
    const meals = [
      meal("Breakfast", [part(first("breakfast"), slotTotal("breakfast")), part(first("milk"), milkAtBreakfast), part(first("fruitveg"), slotTotal("fruitveg"))]),
      meal("Lunch", [part(entrees[0], perMeal), byKey("rice") ? staplePart(byKey("rice"), 1) : null, ...(byKey("rice") ? [] : others.map((x) => staplePart(x, 0.5)))]),
      meal("Dinner", [part(entrees[1] || entrees[0], perMeal), ...others.map((x) => staplePart(x, byKey("rice") ? 1 : 0.5))]),
    ];
    const rest = [part(first("milk"), milk - milkAtBreakfast)];
    lines.filter((l) => l.slot === "basics" || l.slot === "extras").forEach((l) => rest.push(part(l, perDay(l))));
    if (rest.some(Boolean)) meals.push(meal("Through the day", rest));
    return meals.filter((x) => x.parts.length);
  }
  function fmtServings(n) {
    if (n >= 0.95 && n <= 1.05) return "1 serving of";
    if (n < 1) return `${fmtFrac(n)} serving of`;
    return `${fmtFrac(n)} servings of`;
  }
  function fmtFrac(n) {
    const whole = Math.floor(n), rem = n - whole;
    const q = Math.round(rem * 4) / 4;
    const fr = { 0: "", 0.25: "¼", 0.5: "½", 0.75: "¾" }[q];
    if (q === 1) return String(whole + 1);
    return (whole ? whole : "") + (fr || "") || "0";
  }
  function fmtCups(c) {
    const r = Math.max(0.25, Math.round(c * 4) / 4);
    return `${fmtFrac(r)} ${r <= 1 ? "cup" : "cups"}`;
  }

  // "Nx Vitamin Milk #10 Cans (…)" lines → { handle: cans } using the kit's own product list.
  function kitQuantities(k) {
    if (!k || !k.contents || !k.contents.length) return null;
    const norm = (s) => s.toLowerCase().replace(/\(.*?\)/g, " ").replace(/[^a-z0-9# ]+/g, " ").split(/\s+/)
      .map((w) => (w in cfg.kitAliases ? cfg.kitAliases[w] : w)).filter((w) => w && w.length > 2);
    const pool = (k.products && k.products.length ? k.products : catalog.cans.map((c) => c.handle))
      .map((hd) => catalog.cans.find((c) => c.handle === hd)).filter(Boolean)
      .map((c) => ({ c, words: new Set(norm(c.title)) }));
    const out = {};
    let matched = 0;
    k.contents.forEach((line) => {
      const m = /(\d+)\s*[x×]\s*(.+)/i.exec(line);
      if (!m) return;
      const n = parseInt(m[1], 10);
      const words = norm(m[2]);
      let best = null, bestScore = 0;
      pool.forEach((p) => {
        const score = words.reduce((s, w) => s + (p.words.has(w) ? 1 : 0), 0);
        if (score > bestScore) { bestScore = score; best = p.c; }
        else if (score === bestScore && best && score > 0 && p.words.size < new Set(norm(best.title)).size) best = p.c; // tie → shorter title
      });
      if (best && bestScore > 0 && n > 0) { out[best.handle] = (out[best.handle] || 0) + n; matched++; }
    });
    const lineCount = k.contents.filter((line) => /(\d+)\s*[x×]\s*(.+)/i.test(line)).length;
    if (!matched || matched < lineCount) return null; // a partial match would silently drop products
    // Under the current restrictions, is there anything left of the kit?
    return Object.keys(out).some((hd) => { const c = catalog.cans.find((x) => x.handle === hd); return c && allowed(c); }) ? out : null;
  }

  /* ---------- rendering ---------- */
  function h(strings, ...vals) {
    return strings.reduce((out, s, i) => out + s + (i < vals.length ? vals[i] : ""), "");
  }
  const W = (k) => (cfg.words && cfg.words[k]) || DEFAULTS.words[k];
  function L(key) {
    const v = cfg.labels && cfg.labels[key];
    return v == null || String(v).trim() === "" ? DEFAULTS.labels[key] : String(v);
  }
  function cartLabel(n, short) {
    if (short) return "Add to cart";
    return L("cart").replace("{n} cans", `${n} ${plural(n, "can")}`).replace("{n}", String(n));
  }
  function buyLink(item) {
    if (!item || !item.url) return "";
    return h` <a class="hmc-buy" href="${esc(item.url)}" target="_blank" rel="sponsored noopener">${esc(item.urlLabel || "Where to buy")}</a>`;
  }
  const cap = (s) => String(s).charAt(0).toUpperCase() + String(s).slice(1);
  function peopleText() {
    const a = `${state.adults} ${plural(state.adults, "adult")}`;
    return state.kids ? `${a}, ${state.kids} ${plural(state.kids, "kid")}` : a;
  }
  function thumb(c, size) {
    const name = displayName(c);
    const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("");
    return cfg.images && c.img
      ? h`<img class="hmc-thumb ${size || ""}" src="${esc(c.img)}" alt="" loading="lazy">`
      : h`<span class="hmc-thumb hmc-thumb-txt ${size || ""}" aria-hidden="true">${esc(initials)}</span>`;
  }

  function renderInputs(r) {
    const cal = cfg.calories;
    return h`
      <section class="hmc-card hmc-inputs" aria-labelledby="hmc-in-title">
        <h2 id="hmc-in-title" class="hmc-h2" tabindex="-1">${esc(L("inputs"))}</h2>
        <div class="hmc-field">
          <div class="hmc-label" id="hmc-people-label">People</div>
          <div class="hmc-people">
            <div class="hmc-stepper-wrap">
              <label class="hmc-sublabel" for="hmc-adults">Adults and teens</label>
              <div class="hmc-stepper">
                <button type="button" class="hmc-step" data-act="adults" data-delta="-1" aria-label="Fewer adults">&minus;</button>
                <input id="hmc-adults" class="hmc-num" type="number" inputmode="numeric" min="1" max="${cfg.maxAdults}" value="${state.adults}">
                <button type="button" class="hmc-step" data-act="adults" data-delta="1" aria-label="More adults">+</button>
              </div>
            </div>
            <div class="hmc-stepper-wrap">
              <label class="hmc-sublabel" for="hmc-kids">Kids under 12</label>
              <div class="hmc-stepper">
                <button type="button" class="hmc-step" data-act="kids" data-delta="-1" aria-label="Fewer kids">&minus;</button>
                <input id="hmc-kids" class="hmc-num" type="number" inputmode="numeric" min="0" max="${cfg.maxKids}" value="${state.kids}">
                <button type="button" class="hmc-step" data-act="kids" data-delta="1" aria-label="More kids">+</button>
              </div>
            </div>
          </div>
          <p class="hmc-hint">A child counts as half an adult for food and a full person for drinking water.</p>
          ${r.largeGroup ? h`<p class="hmc-notice" role="status">${esc(L("largeGroup"))}</p>` : ""}
        </div>
        <div class="hmc-field">
          <label class="hmc-label" for="hmc-days">Days</label>
          <div class="hmc-chips" role="group" aria-label="Common lengths">
            ${cfg.dayPresets.map((d) => h`<button type="button" class="hmc-chip ${state.days === d ? "is-on" : ""}" data-act="days" data-value="${d}" aria-pressed="${state.days === d}">${d === 365 ? "1 year" : d + " days"}</button>`).join("")}
          </div>
          <div class="hmc-range-row">
            <input id="hmc-days" class="hmc-range" type="range" min="1" max="365" value="${state.days}" aria-valuetext="${state.days} days">
            <input id="hmc-days-num" class="hmc-num" type="number" inputmode="numeric" min="1" max="365" value="${state.days}" aria-label="Days">
          </div>
        </div>
        <div class="hmc-field">
          <div class="hmc-label" id="hmc-cal-label">Daily calories per person</div>
          <div class="hmc-cards" role="radiogroup" aria-labelledby="hmc-cal-label">
            ${cal.map((c) => h`<button type="button" role="radio" aria-checked="${state.kcal === c.key}" class="hmc-opt ${state.kcal === c.key ? "is-on" : ""}" tabindex="${state.kcal === c.key ? 0 : -1}" data-act="kcal" data-value="${esc(c.key)}"><span class="hmc-opt-title">${esc(c.label)}</span><span class="hmc-opt-num">${num(c.kcal)}</span><span class="hmc-opt-hint">${esc(c.hint || "")}</span></button>`).join("")}
          </div>
        </div>
        <div class="hmc-field">
          <div class="hmc-label" id="hmc-meal-label">Lunch and dinner</div>
          <div class="hmc-cards hmc-cards-2" role="radiogroup" aria-labelledby="hmc-meal-label">
            <button type="button" role="radio" aria-checked="${state.staples}" class="hmc-opt ${state.staples ? "is-on" : ""}" tabindex="${state.staples ? 0 : -1}" data-act="meals" data-value="staples"><span class="hmc-opt-title">One can serving plus ${esc(W("lower"))}</span><span class="hmc-opt-hint">Our cans carry the nutrition. The rice, pasta, and beans you already own carry the rest of the calories. Fewer cans, lower cost.</span></button>
            <button type="button" role="radio" aria-checked="${!state.staples}" class="hmc-opt ${!state.staples ? "is-on" : ""}" tabindex="${!state.staples ? 0 : -1}" data-act="meals" data-value="cans"><span class="hmc-opt-title">Two can servings, cans only</span><span class="hmc-opt-hint">Nothing to buy elsewhere and less cooking. About twice the entree cans.</span></button>
          </div>
        </div>
        <div class="hmc-field">
          <div class="hmc-label" id="hmc-res-label">Anything you can't eat?</div>
          <div class="hmc-chips" role="group" aria-labelledby="hmc-res-label">
            ${cfg.restrictions.map((x) => h`<button type="button" class="hmc-chip ${state.restrictions.includes(x.key) ? "is-on" : ""}" data-act="restrict" data-value="${esc(x.key)}" aria-pressed="${state.restrictions.includes(x.key)}">${esc(x.label)}</button>`).join("")}
          </div>
        </div>
      </section>`;
  }

  function productRow(l) {
    const c = l.c;
    const name = displayName(c);
    const meta = `${num(l.servings)} ${plural(l.servings, "serving")}` + (l.why ? " · " + esc(l.why) : "") + (l.edited && l.recommended ? ` · we suggested ${l.recommended}` : l.recommended === 0 ? " · added by you" : "");
    return h`
      <li class="hmc-row">
        <button type="button" class="hmc-row-open" data-act="open" data-value="${esc(c.handle)}" aria-label="About ${esc(name)}">${thumb(c)}</button>
        <div class="hmc-row-main">
          <button type="button" class="hmc-row-name hmc-row-open" data-act="open" data-value="${esc(c.handle)}">${esc(name)}</button>
          <span class="hmc-row-meta">${meta}</span>
        </div>
        <div class="hmc-qty" role="group" aria-label="${esc(name)} cans">
          <button type="button" class="hmc-qty-btn" data-act="qty" data-value="${esc(c.handle)}" data-delta="-1" aria-label="One less can of ${esc(name)}">&minus;</button>
          <span class="hmc-qty-n" aria-live="off">${l.cans}</span>
          <button type="button" class="hmc-qty-btn" data-act="qty" data-value="${esc(c.handle)}" data-delta="1" aria-label="One more can of ${esc(name)}">+</button>
        </div>
        <span class="hmc-row-price">${money(l.cans * c.price)}</span>
        <button type="button" class="hmc-x" data-act="remove" data-value="${esc(c.handle)}" aria-label="Remove ${esc(name)} from the plan">&times;</button>
      </li>`;
  }

  function renderPlan(r) {
    const removed = state.removed.map((hd) => catalog.cans.find((c) => c.handle === hd)).filter(Boolean);
    const gapText = (g) => g.reason === "diet"
      ? (state.staples || g.slot.key !== "entree"
        ? `Nothing in ${esc(g.slot.label.toLowerCase())} fits what you can't eat. We won't pad the plan to hide that.${g.slot.key === "entree" ? ` Rice, pasta, and beans from your ${esc(W("listLower"))} carry those meals.` : ""}`
        : `Nothing for lunch and dinner fits what you can't eat, and cans only is on, so those meals are empty. Rice, pasta, and beans can carry them. <button type="button" class="hmc-link" data-act="staples-on">Use rice, pasta &amp; beans</button>`)
      : g.reason === "kit"
      ? `The kit has no ${esc(g.slot.label.toLowerCase())}. Use "Back to our suggestion" to open the plan up again, or cover it from your pantry.`
      : `You removed everything in ${esc(g.slot.label.toLowerCase())}. Restore one below or cover it from your pantry.`;
    return h`
      <section class="hmc-card" aria-labelledby="hmc-plan-title">
        <div class="hmc-card-head">
          <h2 id="hmc-plan-title" class="hmc-h2" tabindex="-1">${esc(L("plan"))}</h2>
          <span class="hmc-muted">${r.canCount} ${plural(r.canCount, "can")} · ${num(r.servings)} servings${r.edited ? h` · <button type="button" class="hmc-link" data-act="reset-qty">Back to our suggestion</button>` : ""}</span>
        </div>
        ${state.days < 14 ? h`<p class="hmc-notice" role="status">Short plan, whole cans. Each can holds 10 to 50 servings, so a ${state.days}-${plural(state.days, "day")} plan leaves most of every can for later. Nothing is wasted: unopened cans keep 25 years, and an opened can keeps for months with the lid on.</p>` : ""}
        ${r.gaps.map((g) => h`<p class="hmc-notice" role="status">${gapText(g)}</p>`).join("")}
        ${r.groups.map((g) => {
          const rows = r.lines.filter((l) => l.slot === g.key);
          if (!rows.length && !g.addable.length) return "";
          if (!rows.length && (g.key === "extras" || g.key === "basics")) {
            return h`<div class="hmc-group hmc-group-empty"><div class="hmc-group-head"><h3 class="hmc-h3">${esc(g.label)}</h3><span class="hmc-muted">${esc(g.why)}</span></div>${addMenu(g)}</div>`;
          }
          const cov = g.needed ? h`<span class="hmc-cov ${g.coverage < 90 ? "is-low" : ""}">${num(g.provided)} of ${num(g.needed)} servings · ${g.coverage}%</span>` : "";
          return h`
            <div class="hmc-group">
              <div class="hmc-group-head"><h3 class="hmc-h3">${esc(g.label)}</h3><span class="hmc-muted">${esc(g.why)}</span>${cov}</div>
              <ul class="hmc-rows">${rows.map(productRow).join("")}</ul>
              ${addMenu(g)}
            </div>`;
        }).join("")}
        ${state.basics ? "" : h`<p class="hmc-muted">Kitchen basics are off. <button type="button" class="hmc-link" data-act="basics" data-value="1">Add them back</button></p>`}
        ${removed.length ? h`
          <div class="hmc-removed">
            <span class="hmc-muted">Removed:</span>
            ${removed.map((c) => h`<button type="button" class="hmc-chip hmc-chip-sm" data-act="restore" data-value="${esc(c.handle)}">${esc(displayName(c))} <span aria-hidden="true">↺</span><span class="hmc-sr"> restore</span></button>`).join("")}
          </div>` : ""}
      </section>`;
  }

  function addMenu(g) {
    if (!g.addable.length) return "";
    return h`
      <div class="hmc-add">
        <label class="hmc-sr" for="hmc-add-${g.key}">Add to ${esc(g.label)}</label>
        <select id="hmc-add-${g.key}" class="hmc-select" data-act="add-select" data-group="${g.key}">
          <option value="">Add another…</option>
          ${g.addable.map((c) => h`<option value="${esc(c.handle)}">${esc(displayName(c))} · ${c.servings} servings · ${money(c.price)}</option>`).join("")}
        </select>
        <button type="button" class="hmc-btn hmc-btn-ghost hmc-btn-sm" data-act="add" data-value="" disabled>Add</button>
      </div>`;
  }

  function renderStaples(r) {
    const totalLbs = r.staples.reduce((s, x) => s + x.lbs, 0);
    const present = new Set(r.lines.map((l) => l.c.handle));
    const seen = new Set();
    const pairs = cfg.pairings.filter((p) => p.with.every((hd) => present.has(hd)) && (!p.anyOf || p.anyOf.some((hd) => present.has(hd))))
      .filter((p) => !seen.has(p.text) && seen.add(p.text));
    const cansPct = Math.min(100, Math.round((r.calPerDay / r.target) * 100));
    const stPct = Math.min(100 - cansPct, Math.round((r.staplesKcalPerDay / r.target) * 100));
    const shortPct = Math.max(0, 100 - cansPct - stPct);
    const bar = h`
      <div class="hmc-calbar" role="img" aria-label="${num(r.calPerDay)} calories a day from cans, ${num(r.staplesKcalPerDay)} from ${esc(W("short"))}, target ${num(r.target)}">
        <div class="hmc-calbar-track">
          <span class="hmc-calbar-cans" style="width:${cansPct}%"></span>
          <span class="hmc-calbar-staples" style="width:${stPct}%"></span>
          <span class="hmc-calbar-short" style="width:${shortPct}%"></span>
        </div>
        <div class="hmc-calbar-legend">
          <span><i class="hmc-sw hmc-sw-cans"></i>Cans ${num(r.calPerDay)}</span>
          ${r.staplesKcalPerDay > 0 ? h`<span><i class="hmc-sw hmc-sw-staples"></i>${esc(cap(W("short")))} ${num(r.staplesKcalPerDay)}</span>` : ""}
          ${shortPct > 0 ? h`<span><i class="hmc-sw hmc-sw-short"></i>Short ${num(Math.max(0, r.target - r.totalCalPerDay))}</span>` : ""}
          <span class="hmc-calbar-target">Target ${num(r.target)} a day per adult${state.kids ? ", half for kids" : ""}</span>
        </div>
      </div>`;
    return h`
      <section class="hmc-card" aria-labelledby="hmc-st-title">
        <div class="hmc-card-head">
          <h2 id="hmc-st-title" class="hmc-h2" tabindex="-1">${esc(L("staples"))}</h2>
          <span class="hmc-muted">${state.staples ? "On. Change it under Lunch and dinner above." : "Off. Change it under Lunch and dinner above."}</span>
        </div>
        ${bar}
        <details class="hmc-why" id="hmc-why" ${state.staples ? "open" : ""}>
          <summary class="hmc-why-summary">Why ${esc(W("lower"))}?</summary>
          <ol class="hmc-why-list">${(cfg.staplesWhy || []).map((t) => h`<li>${esc(t)}</li>`).join("")}</ol>
        </details>
        ${state.staples ? h`
          <p class="hmc-p">Your cans give about ${num(r.calPerDay)} calories and ${num(r.proteinPerDay)} g of protein a day. ${r.gapPerDay > 0 && r.staples.length ? `Staples add the other ${num(r.gapPerDay)} calories to reach ${num(r.target)}, and ${num(r.staplesProteinPerDay)} g more protein.` : r.gapPerDay > 0 ? "" : `That already reaches ${num(r.target)}, so none are needed.`}</p>
          ${r.gapPerDay > 0 && r.staples.length ? h`<p class="hmc-notice"><strong>Sold separately.</strong> We don't sell these. Buy them at any grocery or warehouse store, and store them sealed with oxygen absorbers. Only the cans go in your cart.</p>` : ""}
          ${r.gapPerDay > 0 && !r.staples.length ? h`<p class="hmc-notice">The rice, pasta, and beans are all left out, so this plan runs about ${num(r.gapPerDay)} calories a day short of ${num(r.target)}.</p><div class="hmc-actions-row"><button type="button" class="hmc-btn hmc-btn-ghost" data-act="staples-on">Add rice, pasta &amp; beans back</button></div>` : ""}
          ${r.gapPerDay <= 0 || !r.staples.length ? "" : h`
          <ul class="hmc-rows">
            ${r.staples.map(({ s, lbs }) => h`
              <li class="hmc-row hmc-row-staple">
                <span class="hmc-thumb hmc-thumb-txt" aria-hidden="true">${esc(s.label[0])}</span>
                <div class="hmc-row-main">
                  <span class="hmc-row-name">${esc(s.label)}</span>
                  <span class="hmc-row-meta">${esc(s.shelf)} · ${esc(s.cook)}</span>
                </div>
                <span class="hmc-row-price hmc-lbs">${num(lbs)} lb${buyLink(s)}</span>
                <button type="button" class="hmc-x" data-act="staple-remove" data-value="${esc(s.key)}" aria-label="Leave out ${esc(s.label)}">&times;</button>
              </li>`).join("")}
          </ul>
          <p class="hmc-muted">About ${num(totalLbs)} lb, roughly ${money0(r.staplesCost)} at a warehouse club, not included in your cart.</p>
          ${state.staplesRemoved.length ? h`<div class="hmc-removed"><span class="hmc-muted">Left out:</span>${state.staplesRemoved.map((k) => { const s = cfg.staples.find((x) => x.key === k); return s ? h`<button type="button" class="hmc-chip hmc-chip-sm" data-act="staple-restore" data-value="${esc(k)}">${esc(s.label)} <span aria-hidden="true">↺</span><span class="hmc-sr"> restore</span></button>` : ""; }).join("")}</div>` : ""}
          `}
        ` : h`
          ${state.autoCans ? h`<p class="hmc-notice" role="status">You took out the rice, pasta, and beans, so every lunch and dinner is now two servings from our cans.</p>` : ""}
          <p class="hmc-p">${r.lines.some((l) => l.slot === "entree") ? "Every lunch and dinner is two servings from our cans, so there is nothing to buy elsewhere." : "Cans only is on, but nothing for lunch and dinner fits what you can't eat, so those meals come up empty."}${r.target - r.totalCalPerDay > 25 ? ` That lands about ${num(r.target - r.totalCalPerDay)} calories a day under your target of ${num(r.target)}.` : ""}</p>
          <div class="hmc-actions-row">
            ${canTopUp(r) ? h`<button type="button" class="hmc-btn hmc-btn-primary hmc-btn-inline" data-act="fill-gap">Top up with cans</button>` : ""}
            <button type="button" class="hmc-btn ${canTopUp(r) ? "hmc-btn-ghost" : "hmc-btn-primary hmc-btn-inline"}" data-act="staples-on">${state.autoCans ? "Add rice, pasta &amp; beans back" : "Use rice, pasta &amp; beans"}</button>
          </div>
        `}
        ${renderDay(r)}
        ${pairs.length ? h`<div class="hmc-pairs"><h3 class="hmc-h3">Goes together</h3><p class="hmc-muted">Cereal and fruit are good on their own. These are the combinations our customers like.</p><ul>${pairs.map((p) => h`<li>${esc(p.text)}</li>`).join("")}${cfg.meatNote ? h`<li>${esc(cfg.meatNote)}</li>` : ""}</ul></div>` : ""}
      </section>`;
  }

  function renderDay(r, open) {
    if (!r.day) return "";
    const total = r.day.reduce((s, m) => s + m.cal, 0), prot = r.day.reduce((s, m) => s + m.protein, 0);
    return h`
      <details class="hmc-day hmc-why" id="hmc-day" ${open ? "open" : ""}>
        <summary class="hmc-why-summary">${esc(L("day"))}<span class="hmc-muted hmc-why-hint">One adult, from what is in your plan${state.kids ? ". Kids eat about half." : ""}</span></summary>
        <ul class="hmc-day-list">
          ${r.day.map((m) => h`
            <li class="hmc-day-meal">
              <span class="hmc-day-title">${esc(m.title)}</span>
              <span class="hmc-day-parts">${m.parts.map((p) => esc(p.text)).join(" · ")}</span>
              <span class="hmc-day-nums">${num(m.cal)} cal · ${num(m.protein)} g protein</span>
            </li>`).join("")}
          <li class="hmc-day-meal hmc-day-total"><span class="hmc-day-title">Day</span><span class="hmc-day-parts">Swap products day to day.</span><span class="hmc-day-nums">${num(total)} cal · ${num(prot)} g protein</span></li>
        </ul>
      </details>`;
  }

  function stars(rating, count, href) {
    if (!rating || !count) return "";
    const pctv = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
    const inner = h`<span class="hmc-stars" style="--hmc-stars:${pctv}%" aria-hidden="true"><span class="hmc-stars-on">★★★★★</span><span class="hmc-stars-off">★★★★★</span></span><span class="hmc-stars-count">${Number(rating).toFixed(1)} · ${num(count)} ${plural(Number(count), "review")}</span>`;
    return href ? h`<a class="hmc-stars-row" href="${esc(href)}" aria-label="${Number(rating).toFixed(1)} out of 5 stars, ${num(count)} reviews">${inner}</a>` : h`<span class="hmc-stars-row">${inner}</span>`;
  }

  function productModal(handle) {
    const c = catalog.cans.find((x) => x.handle === handle);
    if (!c) return;
    const r = compute();
    const line = r.lines.find((l) => l.c.handle === handle);
    const name = displayName(c);
    const prep = c.rte ? "Ready to eat, no water needed." : c.hot ? `Add ${c.waterAmt ? c.waterAmt + " " + (c.waterUnit || "cup").toLowerCase() + (c.waterAmt > 1 ? "s" : "") + " of" : ""} hot water${c.standMin ? ", stand " + c.standMin + " minutes" : ""}.` : c.cold ? `Mix with ${c.waterAmt ? c.waterAmt + " " + (c.waterUnit || "cup").toLowerCase() + (c.waterAmt > 1 ? "s" : "") + " of" : ""} cold water.` : "";
    const facts = [
      ["Servings per can", num(c.servings)],
      ["Calories per serving", num(c.cal)],
      ["Protein per serving", `${num(c.protein)} g`],
      ["Allergens", c.allergens.length ? c.allergens.map((a) => a[0].toUpperCase() + a.slice(1)).join(", ") : "None listed"],
      ["Shelf life", c.shelf ? `${c.shelf} years unopened` : "—"],
      ["Price", money(c.price) + " per can"],
    ];
    const feats = (c.features || []).filter(Boolean);
    const html = h`
      <div class="hmc-pm">
        <div class="hmc-pm-media">${cfg.images && (c.img2 || c.img) ? h`<img src="${esc(c.img2 || c.img)}" alt="${esc(name)}">` : thumb(c, "hmc-thumb-lg")}</div>
        <div class="hmc-pm-body">
          ${c.sub ? h`<div class="hmc-eyebrow">${esc(c.sub)}</div>` : ""}
          <h2 class="hmc-h2">${esc(name)}</h2>
          ${stars(c.rating, c.ratingCount, (c.url || `/products/${c.handle}`) + "#judgeme_product_reviews")}
          ${c.highlight ? h`<p class="hmc-pm-highlight">${esc(c.highlight)}</p>` : ""}
          ${c.desc ? h`<p class="hmc-p">${esc(c.desc)}</p>` : ""}
          ${feats.length ? h`<ul class="hmc-pm-feats">${feats.map((f) => h`<li>${esc(f)}</li>`).join("")}</ul>` : ""}
          <dl class="hmc-pm-facts">${facts.map(([k, v]) => h`<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join("")}</dl>
          ${prep ? h`<p class="hmc-muted">${esc(prep)}</p>` : ""}
          <div class="hmc-actions-row">
            <button type="button" class="hmc-btn hmc-btn-primary" data-act="qty" data-value="${esc(c.handle)}" data-delta="1" data-close="1">${line ? `One more can (now ${line.cans})` : "Add to my plan"}</button>
            <a class="hmc-btn hmc-btn-ghost" href="${esc(c.url || `/products/${c.handle}`)}" target="_blank" rel="noopener">Product page<span class="hmc-sr"> (opens in a new tab)</span></a>
          </div>
        </div>
      </div>`;
    showDialog(null, null, html, "hmc-dialog-wide");
    track("hmc_product_view", r, { hmc_handle: handle });
  }

  const EDUCATION_DEFAULT = (links, rating) => h`
    <div class="hmc-edu">
      <div class="hmc-edu-col"><h4>Who we are</h4><p>Nutrient Survival makes food built around nutrition first: every serving is designed to deliver the 40 essential nutrients a body needs, sealed in #10 cans that keep for 25 years. It started with a simple question: if you only had this to eat, would it actually keep you strong?</p><a href="${esc(links.about)}">About us</a></div>
      <div class="hmc-edu-col"><h4>What is in the cans</h4><p>Real ingredients, freeze-dried and powdered, with vitamins, minerals, amino acids, omega-3s and fiber built into each recipe. No artificial preservatives. Most meals need only hot water; milk, eggs, fruit and the shakes work cold.</p><a href="${esc(links.nutrition)}">Our nutrition</a></div>
      <div class="hmc-edu-col"><h4>What customers say</h4><p>${rating ? `Rated ${rating.value} out of 5 across ${num(rating.count)} reviews on our build-your-own kit.` : "Thousands of families and pros keep our cans on the shelf."} People mention the taste first, then that they can actually eat it every day.</p><a href="${esc(links.reviews)}">Read reviews</a></div>
      <div class="hmc-edu-col"><h4>How we think about preparedness</h4><p>Calories keep you alive; nutrients keep you capable. That is why this planner pairs our cans with cheap rice, pasta, and beans: the cans carry the nutrition, the grocery basics carry the bulk, and you end up with food you would happily eat on a normal Tuesday.</p><a href="${esc(links.opsGrade)}">Special Ops grade</a></div>
    </div>`;

  function renderEducation(r) {
    const rating = (() => {
      const kit = catalog.cans.find((c) => c.handle === "custom-6-can-bundle");
      const withRatings = catalog.cans.filter((c) => c.rating && c.ratingCount);
      if (!withRatings.length) return null;
      const count = withRatings.reduce((s, c) => s + Number(c.ratingCount), 0);
      const avg = withRatings.reduce((s, c) => s + Number(c.rating) * Number(c.ratingCount), 0) / count;
      return { value: avg.toFixed(1), count, kit };
    })();
    const body = cfg.educationHtml && String(cfg.educationHtml).trim() ? String(cfg.educationHtml) : EDUCATION_DEFAULT(cfg.educationLinks, rating);
    return h`
      <details class="hmc-card hmc-details" id="hmc-edu">
        <summary class="hmc-h2 hmc-details-summary">${esc(L("education"))}</summary>
        <div class="hmc-edu-wrap">${body}</div>
      </details>`;
  }

  function renderWater(r) {
    return h`
      <section class="hmc-card" aria-labelledby="hmc-w-title">
        <h2 id="hmc-w-title" class="hmc-h2">${esc(L("water"))}</h2>
        <div class="hmc-stats">
          <div class="hmc-stat"><span class="hmc-stat-v">${num(r.cookGal)}</span><span class="hmc-stat-k">gallons to prepare the cans</span></div>
          ${r.stapleGal > 0 ? h`<div class="hmc-stat"><span class="hmc-stat-v">${num(r.stapleGal)}</span><span class="hmc-stat-k">gallons to cook ${esc(W("short"))}</span></div>` : ""}
          <div class="hmc-stat"><span class="hmc-stat-v">${num(r.drinkGal)}</span><span class="hmc-stat-k">gallons to drink</span></div>
          <div class="hmc-stat hmc-stat-total"><span class="hmc-stat-v">${num(r.totalGal)}</span><span class="hmc-stat-k">gallons in total</span></div>
        </div>
        <p class="hmc-p">Drinking water is figured at one gallon per person per day. Here is what ${num(r.totalGal)} gallons looks like in containers.</p>
        <ul class="hmc-containers">
          ${cfg.containers.map((ct) => {
            const n = Math.ceil(r.totalGal / ct.gal);
            return h`
              <li class="hmc-container">
                <span class="hmc-container-n">${n}</span>
                <span class="hmc-container-l">${esc(ct.label)}${n === 1 ? "" : "s"}</span>
                <span class="hmc-container-c">${esc(ct.cost)}${buyLink(ct)}</span>
                <button type="button" class="hmc-tip" data-act="tip" aria-expanded="false" aria-controls="hmc-tip-${ct.key}" aria-label="About the ${esc(ct.label)}">?</button>
                <div class="hmc-tip-pop" id="hmc-tip-${ct.key}" role="note" hidden>${esc(ct.note)}</div>
              </li>`;
          }).join("")}
        </ul>
      </section>`;
  }

  function tipsList(r) {
    const shelves = {};
    r.lines.forEach((l) => { const y = String(l.c.shelf || "25"); (shelves[y] = shelves[y] || []).push(displayName(l.c)); });
    const years = Object.keys(shelves).sort((a, b) => b - a);
    const shelfText = years.length <= 1
      ? `Our #10 cans keep for ${years[0] || "25"} years unopened.`
      : `Our #10 cans keep for ${years[0]} years unopened, except ${years.slice(1).map((y) => shelves[y].join(", ") + " at " + y + " years").join(" and ")}.`;
    return [
      `${shelfText} Store them cool, dry, and off concrete floors. A closet beats a garage that swings hot and cold.`,
      `${r.hotItems} of your ${r.lines.length} products want hot water. Plan a heat source and fuel: a camp stove, a rocket stove, or a propane burner.`,
      "Milk, whey, tomato, and the freeze-dried fruit work with cold water, so you can eat before the stove is lit.",
      "Keep rice, pasta, and beans in food-grade buckets with mylar liners and oxygen absorbers. Label the date. Rotate rice and pasta into everyday cooking.",
      "Rotate stored water once a year. Two drops of unscented bleach per quart treats water you are unsure about.",
      "Print this plan and tape it inside the pantry door so anyone in the house can cook from it.",
    ];
  }

  function renderTips(r) {
    return h`
      <details class="hmc-card hmc-details" id="hmc-tips">
        <summary class="hmc-h2 hmc-details-summary">${esc(L("tips"))}</summary>
        <ul class="hmc-tips">${tipsList(r).map((t) => h`<li>${esc(t)}</li>`).join("")}</ul>
      </details>`;
  }

  function renderKit(r) {
    if (!r.kit) return "";
    const k = r.kit;
    const people = Math.max(1, Math.ceil(foodPeople()));
    const price = k.price * people;
    const href = k.url || `/products/${k.handle}`;
    // Offer "start from this kit" only when every kit product passes the current restrictions.
    const kq = kitQuantities(k);
    const canMatch = !!kq && Object.keys(kq).every((hd) => { const c = catalog.cans.find((x) => x.handle === hd); return c && allowed(c); });
    return h`
      <section class="hmc-card hmc-kit" aria-labelledby="hmc-kit-title">
        <div class="hmc-kit-body">
          <h2 id="hmc-kit-title" class="hmc-h2">${esc(L("kit"))}</h2>
          <p class="hmc-p">${people > 1 ? people + " × " : ""}<strong>${esc(k.title)}</strong> is ${money(price)}, with ${num(k.servings * people)} servings at about ${num(k.calDay)} calories${k.protDay ? ` and ${num(k.protDay)} g protein` : ""} a day per person. Your custom plan is ${money(r.total)} at about ${num(r.totalCalPerDay)} calories and ${num(r.totalProteinPerDay)} g protein a day${state.staples && r.gapPerDay > 0 ? " with staples" : ""}, and you pick every can.</p>
          <div class="hmc-actions-row">
            <a class="hmc-btn hmc-btn-ghost" href="${esc(href)}" target="_blank" rel="noopener">See the kit<span class="hmc-sr"> (opens in a new tab)</span></a>
            ${canMatch ? h`<button type="button" class="hmc-btn hmc-btn-ghost" data-act="match-kit">Start from this kit${people > 1 ? " × " + people : ""}</button>` : ""}
          </div>
        </div>
        ${cfg.images && k.img ? h`<a class="hmc-kit-img" href="${esc(href)}" target="_blank" rel="noopener" tabindex="-1" aria-hidden="true"><img src="${esc(k.img)}" alt="" loading="lazy"></a>` : ""}
      </section>`;
  }

  function renderSummary(r) {
    const nextMsg = r.nextTier
      ? `Add ${r.nextTier[0] - r.canCount} more ${plural(r.nextTier[0] - r.canCount, "can")} to save ${Math.round(r.nextTier[1] * 100)}%.`
      : "You are at our best bundle price.";
    return h`
      <aside class="hmc-sidebar" aria-labelledby="hmc-sum-title">
        <h2 id="hmc-sum-title" class="hmc-h2">${esc(L("summary"))}</h2>
        <p class="hmc-sum-lede">${esc(peopleText())} · ${state.days} ${plural(state.days, "day")}</p>
        <p class="hmc-sum-lede">${num(r.totalCalPerDay)} cal · ${num(r.totalProteinPerDay)} g protein a day per adult${state.kids ? ", about half for each kid" : ""}</p>
        <dl class="hmc-sum">
          <div><dt>${r.canCount} ${plural(r.canCount, "can")}</dt><dd>${money(r.list)}</dd></div>
          ${r.pct ? h`<div class="hmc-sum-save"><dt>Bundle discount ${Math.round(r.pct * 100)}%</dt><dd>&minus;${money(r.discount)}</dd></div>` : ""}
          <div class="hmc-sum-total"><dt>Cans total</dt><dd>${money(r.total)}</dd></div>
          ${r.staples.length ? h`<div><dt>${esc(W("title"))}, sold separately</dt><dd>about ${money0(r.staplesCost)}</dd></div>` : ""}
          <div><dt>Per person per day</dt><dd>${money(r.perPersonDay)}</dd></div>
        </dl>
        ${r.target - r.totalCalPerDay > 25 ? h`<p class="hmc-hint hmc-disclaimer">About ${num(r.target - r.totalCalPerDay)} calories a day under your ${num(r.target)} target. ${canTopUp(r) ? h`<button type="button" class="hmc-link" data-act="fill-gap">Top up with cans</button>` : !state.staples ? h`<button type="button" class="hmc-link" data-act="staples-on">Use rice, pasta &amp; beans</button>` : ""}</p>` : ""}
        <p class="hmc-hint">${nextMsg}${r.total >= cfg.freeShipping ? " Ships free." : ""}</p>
        ${r.staples.length ? h`<p class="hmc-hint hmc-disclaimer">${esc(cap(W("short")))}: sold separately. The cart holds the cans only; rice, pasta, and beans are on your ${esc(W("listLower"))} to buy anywhere.</p>` : ""}
        <button type="button" class="hmc-btn hmc-btn-primary" data-act="cart" ${r.canCount ? "" : "disabled"}>${esc(cartLabel(r.canCount))}</button>
        <button type="button" class="hmc-btn hmc-btn-ghost hmc-btn-wide" data-act="view" data-value="plan">${esc(L("view"))}</button>
        <div class="hmc-actions">
          <button type="button" class="hmc-btn hmc-btn-ghost" data-act="email">${esc(L("email"))}</button>
          <button type="button" class="hmc-btn hmc-btn-ghost" data-act="copy">${esc(L("copy"))}</button>
        </div>
        ${L("payNote") ? h`<p class="hmc-hint">${esc(L("payNote"))}</p>` : ""}
      </aside>`;
  }

  /* ---------- plan presentation view ---------- */
  function renderPlanView(r) {
    const groups = r.groups.filter((g) => r.lines.some((l) => l.slot === g.key));
    const title = `${peopleText()}, ${state.days} ${plural(state.days, "day")}`;
    return h`
      <div class="hmc hmc-planview">
        <header class="hmc-pv-head">
          <div>
            <div class="hmc-eyebrow">Your plan</div>
            <h2 class="hmc-h1 hmc-pv-title" id="hmc-pv-title" tabindex="-1">${esc(title)}</h2>
            <p class="hmc-lede">${r.canCount} ${plural(r.canCount, "can")}, ${num(r.servings)} servings, about ${num(r.totalCalPerDay)} calories and ${num(r.totalProteinPerDay)} g protein a day per adult${r.staples.length ? " with rice, pasta & beans" : ""}. ${state.restrictions.length ? esc("No " + state.restrictions.map((k) => (cfg.restrictions.find((x) => x.key === k) || {}).label || k).join(", ").toLowerCase() + ".") : ""}</p>
          </div>
          <div class="hmc-pv-actions">
            <button type="button" class="hmc-btn hmc-btn-primary" data-act="cart" ${r.canCount ? "" : "disabled"}>${esc(cartLabel(r.canCount))}</button>
            <button type="button" class="hmc-btn hmc-btn-ghost" data-act="view" data-value="build">Edit plan</button>
            <button type="button" class="hmc-btn hmc-btn-ghost" data-act="print">Print</button>
            <button type="button" class="hmc-btn hmc-btn-ghost" data-act="email">${esc(L("email"))}</button>
            <button type="button" class="hmc-btn hmc-btn-ghost" data-act="copy">${esc(L("copy"))}</button>
          </div>
        </header>
        <div class="hmc-pv-stats">
          <div class="hmc-stat"><span class="hmc-stat-v">${money0(r.total)}</span><span class="hmc-stat-k">cans after ${Math.round(r.pct * 100)}% bundle discount</span></div>
          <div class="hmc-stat"><span class="hmc-stat-v">${money(r.perPersonDay)}</span><span class="hmc-stat-k">per person per day, ${esc(W("short"))} included</span></div>
          <div class="hmc-stat"><span class="hmc-stat-v">${num(r.totalGal)}</span><span class="hmc-stat-k">gallons of water to store</span></div>
          <div class="hmc-stat"><span class="hmc-stat-v">${num(r.totalCalPerDay)}</span><span class="hmc-stat-k">calories a day per adult</span></div>
          <div class="hmc-stat"><span class="hmc-stat-v">${num(r.totalProteinPerDay)} g</span><span class="hmc-stat-k">protein a day per adult</span></div>
        </div>
        ${r.day ? h`<section class="hmc-card">${renderDay(r, true)}</section>` : ""}
        ${groups.map((g) => h`
          <section class="hmc-pv-group">
            <div class="hmc-group-head"><h3 class="hmc-h2">${esc(g.label)}</h3><span class="hmc-muted">${esc(g.why)}</span></div>
            <ul class="hmc-pv-grid">
              ${r.lines.filter((l) => l.slot === g.key).map((l) => h`
                <li class="hmc-pv-item">
                  <button type="button" class="hmc-row-open" data-act="open" data-value="${esc(l.c.handle)}" aria-label="About ${esc(displayName(l.c))}">${thumb(l.c, "hmc-thumb-lg")}</button>
                  <span class="hmc-pv-qty">${l.cans}×</span>
                  <span class="hmc-pv-name">${esc(displayName(l.c))}</span>
                  <span class="hmc-pv-meta">${num(l.servings)} servings · ${money(l.cans * l.c.price)}</span>
                </li>`).join("")}
            </ul>
          </section>`).join("")}
        <div class="hmc-pv-cols">
          ${r.staples.length ? h`
          <section class="hmc-card">
            <h3 class="hmc-h2">${esc(W("list"))}</h3>
            <p class="hmc-p">Sold separately, not in your cart. Buy anywhere, store sealed with oxygen absorbers. About ${money0(r.staplesCost)}.</p>
            <ul class="hmc-pv-list">${r.staples.map(({ s, lbs }) => h`<li><strong>${num(lbs)} lb</strong> ${esc(s.label)} <span class="hmc-muted">· ${esc(s.shelf)}</span>${buyLink(s)}</li>`).join("")}</ul>
          </section>` : ""}
          <section class="hmc-card">
            <h3 class="hmc-h2">Water</h3>
            <ul class="hmc-pv-list">
              <li><strong>${num(r.cookGal)} gal</strong> to prepare the cans</li>
              ${r.stapleGal > 0 ? h`<li><strong>${num(r.stapleGal)} gal</strong> to cook ${esc(W("short"))}</li>` : ""}
              <li><strong>${num(r.drinkGal)} gal</strong> to drink, 1 gallon per person per day</li>
              <li><strong>${num(r.totalGal)} gal</strong> in total: ${esc(cfg.containers.map((ct) => `${Math.ceil(r.totalGal / ct.gal)} ${ct.label}${Math.ceil(r.totalGal / ct.gal) === 1 ? "" : "s"}`).join(", or "))}</li>
            </ul>
          </section>
        </div>
        <section class="hmc-card">
          <h3 class="hmc-h2">${esc(L("tips"))}</h3>
          <ul class="hmc-tips">${tipsList(r).map((t) => h`<li>${esc(t)}</li>`).join("")}</ul>
        </section>
      </div>`;
  }

  // Focus and disclosure survive a re-render: remember them by stable keys, restore after.
  function focusKey(el) {
    if (!el || el === document.body || !root.contains(el)) return null;
    if (el.id) return { id: el.id };
    if (el.dataset && el.dataset.act) return { act: el.dataset.act, value: el.dataset.value || "", delta: el.dataset.delta || "" };
    return null;
  }
  function focusByKey(k) {
    if (!k) return false;
    const el = k.id ? root.querySelector("#" + CSS.escape(k.id)) : root.querySelector(`[data-act="${k.act}"][data-value="${CSS.escape(k.value)}"]${k.delta ? `[data-delta="${k.delta}"]` : ""}`);
    if (el) { el.focus({ preventScroll: true }); return true; }
    return false;
  }
  function render(opts) {
    const keep = focusKey(document.activeElement);
    const open = new Set([...root.querySelectorAll("details[open][id]")].map((d) => d.id));
    const tips = new Set([...root.querySelectorAll(".hmc-tip[aria-expanded='true']")].map((b) => b.getAttribute("aria-controls")));
    const r = compute();
    if (state.view === "plan") {
      root.innerHTML = renderPlanView(r);
    } else {
      root.innerHTML = h`
        <div class="hmc">
          <div class="hmc-main">
            ${renderInputs(r)}
            ${renderPlan(r)}
            ${renderStaples(r)}
            ${renderWater(r)}
            ${renderTips(r)}
            ${renderEducation(r)}
            ${renderKit(r)}
          </div>
          ${renderSummary(r)}
          <div class="hmc-bar">
            <span aria-hidden="true"><strong>${money(r.total)}</strong> · ${r.canCount} ${plural(r.canCount, "can")}</span>
            <button type="button" class="hmc-btn hmc-btn-primary" data-act="cart" aria-label="${esc(cartLabel(r.canCount))}, ${money(r.total)}" ${r.canCount ? "" : "disabled"}>${esc(cartLabel(r.canCount, true))}</button>
          </div>
        </div>`;
    }
    root.classList.toggle("is-planview", state.view === "plan");
    open.forEach((id) => { const d = root.querySelector("#" + CSS.escape(id)); if (d) d.open = true; });
    tips.forEach((id) => { const pop = root.querySelector("#" + CSS.escape(id)); const b = root.querySelector(`[aria-controls="${id}"]`); if (pop && b) { pop.hidden = false; b.setAttribute("aria-expanded", "true"); } });
    if (!(opts && opts.focus === false)) focusByKey(keep);
    writeHash();
    applyStickyOffset();
    track("hmc_plan", r);
    return r;
  }

  // Keep the sticky summary below the site's sticky/fixed header.
  function applyStickyOffset() {
    const section = root.closest(".ns-hmc-section") || root;
    let px = Number(cfg.stickyOffset) || 0;
    if (!px) {
      const cands = document.querySelectorAll("[id^='shopify-section-header'], header, .page__header, .header, .site-header, [class*='header']");
      cands.forEach((el) => {
        const cs = getComputedStyle(el);
        if (cs.position !== "fixed" && cs.position !== "sticky") return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 80 && rect.height >= 40 && rect.height < 400 && rect.width >= innerWidth * 0.6) px = Math.max(px, Math.round(rect.bottom));
      });
    }
    section.style.setProperty("--hmc-sticky-top", (px ? px + 12 : 16) + "px");
  }

  /* ---------- analytics ---------- */
  // GA4 on this store is fed by a Shopify custom pixel (sandboxed), which cannot see the page's
  // dataLayer. So every event is published through Shopify's customer-events channel
  // (Shopify.analytics.publish), where the "Help Me Choose → GA4" pixel forwards it to GA4
  // under the shopper's consent settings. The dataLayer push and DOM event stay for anything
  // on the page that listens (and for debugging).
  //
  // Events: hmc_start (first interaction, once per page view), hmc_plan (the plan after the
  // shopper pauses 2s, deduped), hmc_view, hmc_qty, hmc_add, hmc_remove, hmc_product_view,
  // hmc_match_kit, hmc_fill_gap, hmc_meals, hmc_email, hmc_copy, hmc_add_to_cart,
  // hmc_add_to_cart_error. The pixel adds hmc_purchase when an order carries the plan tag.
  let lastPlanKey = "", planTimer = null, started = false;
  function flatPayload(event, r, extra) {
    const out = {
      hmc_adults: state.adults, hmc_kids: state.kids, hmc_days: state.days, hmc_calories: state.kcal,
      hmc_meals: state.staples ? "rice_pasta_beans" : "cans_only", hmc_restrictions: state.restrictions.join(",") || "none",
      hmc_view: state.view,
    };
    if (r) { out.hmc_cans = r.canCount; out.value = Math.round(r.total * 100) / 100; out.currency = "USD"; }
    Object.entries(extra || {}).forEach(([k, v]) => { if (v !== undefined) out[k] = v; });
    return out;
  }
  function emit(event, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event }, payload));
      document.dispatchEvent(new CustomEvent("hmc:track", { detail: Object.assign({ event }, payload) }));
      const sa = window.Shopify && window.Shopify.analytics;
      if (sa && typeof sa.publish === "function") sa.publish(event, payload);
    } catch (e) { /* analytics never breaks the planner */ }
  }
  function track(event, r, extra) {
    try {
      if (event === "hmc_plan") {
        // A page load renders a plan nobody chose yet: only report plans after the first interaction.
        if (!started) return;
        // The plan is re-rendered on every change; report where the shopper settles, not each step.
        clearTimeout(planTimer);
        planTimer = setTimeout(() => {
          const rr = compute();
          const payload = flatPayload("hmc_plan", rr, extra);
          const key = JSON.stringify(payload);
          if (key === lastPlanKey) return;
          lastPlanKey = key;
          emit("hmc_plan", payload);
        }, 2000);
        return;
      }
      emit(event, flatPayload(event, r, extra));
    } catch (e) { /* analytics never breaks the planner */ }
  }
  function markStarted() {
    if (started) return;
    started = true;
    track("hmc_start", compute());
  }

  /* ---------- interaction ---------- */
  // One live region that outlives every re-render, so announcements are real changes.
  // It is for screen readers only. Messages a sighted shopper needs (link copied, cart
  // error, kit loaded) also show as a short toast; plain navigation echoes ("Showing your
  // plan", "Lasagna removed") stay invisible because the page itself already shows them.
  let liveEl = null, toastEl = null, toastTimer = null;
  function announce(msg, opts) {
    if (!liveEl) {
      liveEl = document.createElement("p");
      liveEl.className = "hmc-live";
      liveEl.setAttribute("aria-live", "polite");
      liveEl.setAttribute("role", "status");
      root.insertAdjacentElement("afterend", liveEl);
    }
    liveEl.textContent = "";
    setTimeout(() => { liveEl.textContent = msg; }, 30);
    if (opts && opts.visible) {
      if (!toastEl) {
        toastEl = document.createElement("div");
        toastEl.className = "hmc-toast";
        toastEl.setAttribute("aria-hidden", "true"); // the live region already speaks it
        document.body.appendChild(toastEl);
      }
      toastEl.textContent = msg;
      toastEl.classList.add("is-on");
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toastEl.classList.remove("is-on"), 3200);
    }
  }
  function update(fn, focusSel) {
    fn();
    render({ focus: !focusSel });
    if (focusSel) {
      const el = root.querySelector(focusSel);
      if (el) el.focus({ preventScroll: true });
    }
  }
  function setQty(handle, delta) {
    if (!Number.isFinite(delta) || !delta) return;
    delete state.topUp[handle]; // a hand change makes this quantity the customer's own
    const r = compute();
    const line = r.lines.find((l) => l.c.handle === handle);
    const cur = line ? line.cans : 0;
    const next = clamp(cur + delta, 0, MAX_CANS);
    if (next === 0) {
      // A hand-added product (no recommendation) just leaves; the Add menu offers it again.
      // A recommended product is remembered as removed so the Removed chips can restore it.
      if (line && line.recommended > 0 && !state.removed.includes(handle)) state.removed.push(handle);
      delete state.qty[handle];
      return;
    }
    if (line && next === line.recommended && line.recommended > 0) delete state.qty[handle]; else state.qty[handle] = next;
    track("hmc_qty", r, { hmc_handle: handle, hmc_qty: next });
  }
  // Close a calorie gap with cans: add one can at a time to the lunch-and-dinner product with
  // the fewest cans (then breakfast), re-computing after each, until the target is met.
  function fillGap() {
    let added = 0;
    const start = compute().canCount;
    for (let i = 0; i < 400; i++) {
      const r = compute();
      if (r.target - r.totalCalPerDay <= 5) break;
      if (r.canCount >= Math.ceil(start * 1.5) + 6) break; // past this, cans are the wrong tool for the gap
      const pick = (slot) => r.lines.filter((l) => l.slot === slot && l.cans < MAX_CANS).sort((a, b) => a.cans - b.cans)[0];
      const line = pick("entree");
      if (!line) break;
      if (!(line.c.handle in state.topUp)) state.topUp[line.c.handle] = state.qty[line.c.handle] ?? null;
      state.qty[line.c.handle] = line.cans + 1;
      added++;
    }
    return added;
  }
  // Can a top-up close this gap sensibly? Only with lunch-and-dinner cans, and not when it would
  // take more than half again the plan's cans (one allowed entree × 100 cans is not a plan).
  const canTopUp = (r) => {
    if (r.target - r.totalCalPerDay <= 25) return false;
    const entrees = r.lines.filter((l) => l.slot === "entree");
    if (!entrees.length) return false;
    const perCan = entrees.reduce((s, l) => s + l.c.cal * l.c.servings, 0) / entrees.length;
    const cansNeeded = ((r.target - r.totalCalPerDay) * r.personDays) / Math.max(1, perCan);
    return cansNeeded <= r.canCount * 0.5 + 6;
  };
  // Take back the cans "Top up with cans" added (the rice, pasta and beans carry those calories now).
  function undoTopUp() {
    // Put each topped-up product back exactly as it was: its earlier hand edit, or no override.
    Object.entries(state.topUp).forEach(([hd, before]) => { if (before == null) delete state.qty[hd]; else state.qty[hd] = before; });
    state.topUp = {};
  }
  // Bring a product into the plan: un-remove it, and if nothing recommends it, add one can.
  function addProduct(handle) {
    state.removed = state.removed.filter((x) => x !== handle);
    delete state.qty[handle];
    const line = compute().lines.find((l) => l.c.handle === handle);
    if (!line) state.qty[handle] = 1;
  }

  function onClick(e) {
    const btn = e.target.closest("[data-act]");
    if (btn) markStarted();
    const dlg = document.getElementById("hmc-dialog");
    if (!btn || !(root.contains(btn) || (dlg && dlg.contains(btn)))) {
      if (!e.target.closest(".hmc-tip-pop")) closeTips();
      return;
    }
    if (btn.tagName === "SELECT") return;
    const act = btn.dataset.act, v = btn.dataset.value;
    switch (act) {
      case "adults": update(() => { state.adults = clamp(state.adults + parseInt(btn.dataset.delta, 10), 1, cfg.maxAdults); }, `[data-act="adults"][data-delta="${btn.dataset.delta}"]`); break;
      case "kids": update(() => { state.kids = clamp(state.kids + parseInt(btn.dataset.delta, 10), 0, cfg.maxKids); }, `[data-act="kids"][data-delta="${btn.dataset.delta}"]`); break;
      case "days": update(() => { state.days = parseInt(v, 10); }, `[data-act="days"][data-value="${v}"]`); break;
      case "kcal": update(() => { state.kcal = v; }, `[data-act="kcal"][data-value="${v}"]`); break;
      case "meals": track("hmc_meals", null, { hmc_choice: v }); update(() => {
        state.staples = v === "staples";
        // Choosing the rice/pasta/beans option always means at least some of them.
        if (state.staples && state.staplesRemoved.length >= cfg.staples.length) state.staplesRemoved = [];
        if (state.staples) undoTopUp();
        state.autoCans = false;
      }, `[data-act="meals"][data-value="${v}"]`); break;
      case "restrict": update(() => { const i = state.restrictions.indexOf(v); i >= 0 ? state.restrictions.splice(i, 1) : state.restrictions.push(v); }, `[data-act="restrict"][data-value="${v}"]`); break;
      case "qty": { const c = catalog.cans.find((x) => x.handle === v); const inPlan = !!compute().lines.find((l) => l.c.handle === v); if (btn.dataset.close) { const d = document.getElementById("hmc-dialog"); if (d && d.open) d.close(); } update(() => { if (btn.dataset.close && !inPlan) addProduct(v); else setQty(v, parseInt(btn.dataset.delta, 10)); }, `[data-act="qty"][data-value="${v}"][data-delta="${btn.dataset.delta}"]`); const l = compute().lines.find((x) => x.c.handle === v); announce(l ? `${displayName(c)}: ${l.cans} ${plural(l.cans, "can")}.` : `${displayName(c)} removed.`); break; }
      case "remove": { const c = catalog.cans.find((x) => x.handle === v); update(() => { if (!state.removed.includes(v)) state.removed.push(v); delete state.qty[v]; }, "#hmc-plan-title"); track("hmc_remove", null, { hmc_handle: v }); announce(`${displayName(c)} removed. Plan updated.`); break; }
      case "restore": { const c = catalog.cans.find((x) => x.handle === v); update(() => addProduct(v), "#hmc-plan-title"); announce(`${displayName(c)} is back in the plan.`); break; }
      case "reset-qty": update(() => { state.qty = {}; state.removed = []; state.kit = null; state.topUp = {}; }, "#hmc-plan-title"); announce("Back to our suggested quantities."); break;
      case "basics": update(() => { state.basics = v === "1"; }, "#hmc-plan-title"); break;
      case "staple-remove": {
        const last = cfg.staples.filter((s) => !state.staplesRemoved.includes(s.key) && s.key !== v).length === 0;
        update(() => {
          if (last) {
            // Nothing left to carry the calories: lunch and dinner go back to two can servings.
            state.staples = false;
            state.staplesRemoved = [];
            state.autoCans = true;
          } else if (!state.staplesRemoved.includes(v)) state.staplesRemoved.push(v);
        }, "#hmc-st-title");
        if (last) announce("No rice, pasta, or beans, so lunch and dinner are now two can servings each. You can add them back anytime.", { visible: true });
        break;
      }
      case "staple-restore": update(() => { state.staplesRemoved = state.staplesRemoved.filter((x) => x !== v); }, "#hmc-st-title"); break;
      case "staples-on": track("hmc_meals", null, { hmc_choice: "rice_pasta_beans", hmc_source: "button" }); update(() => { state.staples = true; state.staplesRemoved = []; state.autoCans = false; undoTopUp(); }, "#hmc-st-title"); announce(`Rice, pasta, and beans are back in the plan.`, { visible: true }); break;
      case "fill-gap": { const added = fillGap(); track("hmc_fill_gap", compute(), { hmc_added: added }); update(() => {}, "#hmc-plan-title"); announce(added ? `Added ${added} ${plural(added, "can")} to reach your calorie target.` : "No more cans can be added for this plan.", { visible: true }); break; }
      case "match-kit": matchKit(); break;
      case "view": update(() => { state.view = v === "plan" ? "plan" : "build"; }, v === "plan" ? "#hmc-pv-title" : "#hmc-in-title"); window.scrollTo({ top: (root.closest(".ns-hmc-section") || root).getBoundingClientRect().top + window.scrollY - 20, behavior: "smooth" }); announce(state.view === "plan" ? "Showing your plan." : "Back to the builder."); track("hmc_view", null, { hmc_view: state.view }); break;
      case "print": window.print(); break;
      case "open": productModal(v); break;
      case "add": { if (!v) break; const c = catalog.cans.find((x) => x.handle === v); update(() => addProduct(v), `[data-act="qty"][data-value="${v}"][data-delta="1"]`); track("hmc_add", null, { hmc_handle: v }); announce(`${displayName(c)} added.`); break; }
      case "tip": toggleTip(btn); break;
      case "cart": addToCart(); break;
      case "email": emailPlan(); break;
      case "copy": copyLink(); break;
    }
  }

  function matchKit() {
    const r = compute();
    const q = kitQuantities(r.kit);
    if (!q) return;
    const people = Math.max(1, Math.ceil(foodPeople()));
    update(() => {
      state.qty = {};
      state.removed = [];
      // The kit flag limits every group to the kit's products; kit items get the kit's counts × people.
      state.kit = r.kit.handle;
      Object.entries(q).forEach(([hd, n]) => { const c = catalog.cans.find((x) => x.handle === hd); if (c && allowed(c)) state.qty[hd] = clamp(n * people, 1, MAX_CANS); });
    }, "#hmc-plan-title");
    track("hmc_match_kit", compute(), { hmc_kit: r.kit.handle });
    announce(`Plan set to the ${r.kit.title}${people > 1 ? " × " + people : ""}. Adjust anything you like.`, { visible: true });
  }

  // A field's `change` fires on the blur that a click or Tab causes. Re-rendering right then
  // destroys the element about to receive the click, so the render waits for the pointer to
  // come up (or runs on the next tick for keyboard), and focus is restored by key, not forced.
  let pointerDown = false, pendingRender = null, rangeTimer = null;
  function deferredUpdate(fn) {
    fn();
    if (pointerDown) { pendingRender = () => render(); return; }
    setTimeout(() => render(), 0);
  }
  function onInput(e) {
    const t = e.target;
    if (e.type === "change") markStarted();
    if (t.id === "hmc-adults" && e.type === "change") deferredUpdate(() => { state.adults = clamp(parseInt(t.value, 10) || 1, 1, cfg.maxAdults); });
    if (t.id === "hmc-kids" && e.type === "change") deferredUpdate(() => { state.kids = clamp(parseInt(t.value, 10) || 0, 0, cfg.maxKids); });
    if (t.id === "hmc-days" || t.id === "hmc-days-num") {
      const val = clamp(parseInt(t.value, 10) || 1, 1, 365);
      if (e.type === "input" && t.id === "hmc-days") {
        const n = root.querySelector("#hmc-days-num"); if (n) n.value = val;
        t.setAttribute("aria-valuetext", val + " days");
        return;
      }
      if (e.type === "input") return;
      if (t.id === "hmc-days") { // keyboard steps fire change per key: settle first
        clearTimeout(rangeTimer);
        rangeTimer = setTimeout(() => deferredUpdate(() => { state.days = val; }), 180);
        return;
      }
      deferredUpdate(() => { state.days = val; });
    }
    if (t.dataset && t.dataset.act === "add-select" && e.type === "change") {
      // Choosing an option only enables the Add button; nothing changes until it is pressed.
      const b = t.parentElement && t.parentElement.querySelector('[data-act="add"]');
      if (b) { b.disabled = !t.value; b.dataset.value = t.value || ""; }
    }
  }

  function closeTips() {
    root.querySelectorAll(".hmc-tip[aria-expanded='true']").forEach((b) => {
      b.setAttribute("aria-expanded", "false");
      const pop = root.querySelector("#" + b.getAttribute("aria-controls"));
      if (pop) pop.hidden = true;
    });
  }
  function toggleTip(btn) {
    const open = btn.getAttribute("aria-expanded") === "true";
    closeTips();
    if (!open) {
      btn.setAttribute("aria-expanded", "true");
      const pop = root.querySelector("#" + btn.getAttribute("aria-controls"));
      if (pop) pop.hidden = false;
    }
  }

  function onShopify() {
    return !!(window.Shopify && window.Shopify.shop) || /nutrientsurvival\.com$/.test(location.hostname) || /shopifypreview\.com$/.test(location.hostname);
  }
  function cartItems() {
    return compute().lines.map((l) => ({ id: Number(l.c.id), quantity: l.cans }));
  }

  async function addToCart() {
    const items = cartItems();
    if (!items.length) return;
    const r = compute();
    if (!onShopify()) {
      showDialog("Prototype: add to cart", `On the live site this sends ${items.length} lines to the cart and opens it. The bundle discount applies automatically, we confirmed that on the store.`, `<pre>${esc(JSON.stringify({ items }, null, 2))}</pre>`);
      return;
    }
    const btns = root.querySelectorAll('[data-act="cart"]');
    btns.forEach((b) => { b.disabled = true; b.textContent = "Adding…"; });
    try {
      // Tag the cart: the order shows "Help Me Choose plan" with the link (customer service can open
      // the exact plan), and the GA4 pixel credits the purchase to the planner. A failed tag never
      // blocks the add.
      try {
        const planLink = location.href.replace(/#.*$/, "") + hashFor(Object.assign({}, state, { view: "plan" }));
        await fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attributes: { "Help Me Choose plan": planLink } }) });
      } catch (e) { /* attribution is best effort */ }
      const res = await fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items }) });
      if (!res.ok) throw new Error(await res.text());
      track("hmc_add_to_cart", r);
      location.href = cfg.cartPath;
    } catch (err) {
      track("hmc_add_to_cart_error", r);
      render({ focus: false });
      announce("We couldn't add those to the cart. Please try again or call us.", { visible: true });
    }
  }

  function planSnapshot(r) {
    writeHash();
    const planUrl = location.href.replace(/#.*$/, "") + hashFor(Object.assign({}, state, { view: "plan" }));
    return {
      plan_url: planUrl,
      edit_url: location.href.replace(/#.*$/, "") + hashFor(Object.assign({}, state, { view: "build" })),
      adults: state.adults, kids: state.kids, people: state.adults + state.kids, food_people: foodPeople(),
      days: state.days,
      calorie_target: r.target,
      calories_per_day: Math.round(r.totalCalPerDay),
      protein_per_day: Math.round(r.totalProteinPerDay),
      cans: r.canCount,
      cans_list_price: Math.round(r.list * 100) / 100,
      discount_pct: Math.round(r.pct * 100),
      cans_total: Math.round(r.total * 100) / 100,
      per_person_per_day: Math.round(r.perPersonDay * 100) / 100,
      restrictions: state.restrictions.slice(),
      lines: r.lines.map((l) => `${l.cans} × ${displayName(l.c)} (${num(l.servings)} servings)`),
      staples: r.staples.map((x) => `${num(x.lbs)} lb ${x.s.label}`),
      staples_cost: Math.round(r.staplesCost),
      water_prepare_gal: Math.round(r.cookGal),
      water_staples_gal: Math.round(r.stapleGal),
      water_drink_gal: Math.round(r.drinkGal),
      water_total_gal: Math.round(r.totalGal),
      kit_alternative: r.kit ? r.kit.title : null,
      // Ready-to-print strings for the email template (no filters or math needed in Klaviyo).
      people_text: peopleText(),
      plan_title: `${peopleText()}, ${state.days} ${plural(state.days, "day")}`,
      days_text: `${state.days} ${plural(state.days, "day")}`,
      cans_text: `${r.canCount} ${plural(r.canCount, "can")}`,
      calories_text: `${num(r.totalCalPerDay)} calories a day per adult${state.kids ? ", about half for each kid" : ""}`,
      protein_text: `${num(r.totalProteinPerDay)} g protein a day per adult`,
      cans_total_text: money(r.total),
      discount_text: r.pct ? `${Math.round(r.pct * 100)}% bundle discount, ${money(r.discount)} off` : "",
      per_person_per_day_text: `${money(r.perPersonDay)} per person per day`,
      staples_cost_text: r.staples.length ? `about ${money0(r.staplesCost)}` : "",
      water_total_text: `${num(r.totalGal)} gallons`,
      water_text: `${num(r.totalGal)} gallons in total: ${num(r.drinkGal)} to drink, ${num(r.cookGal)} to prepare the cans${r.stapleGal > 0 ? `, ${num(r.stapleGal)} to cook ${W("short")}` : ""}`,
      containers_text: cfg.containers.map((ct) => `${Math.ceil(r.totalGal / ct.gal)} ${ct.label}${Math.ceil(r.totalGal / ct.gal) === 1 ? "" : "s"}`).join(", or "),
      restrictions_text: state.restrictions.length ? "No " + state.restrictions.map((k) => ((cfg.restrictions.find((x) => x.key === k) || {}).label || k).toLowerCase()).join(", ") : "No restrictions",
      meals_text: state.staples ? "One can serving plus rice, pasta & beans at lunch and dinner" : "Two can servings at lunch and dinner, cans only",
      day_lines: (r.day || []).map((m) => `${m.title}: ${m.parts.map((p) => p.text).join(", ")} — ${num(m.cal)} cal, ${num(m.protein)} g protein`),
    };
  }

  function emailPlan() {
    const k = cfg.klaviyo || {};
    const live = onShopify() && k.companyId;
    if (onShopify() && !k.companyId) { announce("Emailing plans isn't set up yet. Use Copy link instead.", { visible: true }); return; }
    const optin = k.listId ? h`<label class="hmc-check"><input type="checkbox" id="hmc-email-optin"><span>${esc(L("emailOptIn"))}</span></label>` : "";
    showDialog(L("email"), L("emailIntro"), h`
      <label class="hmc-label" for="hmc-email">Email</label>
      <input id="hmc-email" class="hmc-text" type="email" placeholder="you@example.com" autocomplete="email" required inputmode="email">
      ${optin}
      <button type="button" class="hmc-btn hmc-btn-primary" data-dialog-ok>Send my plan</button>
      <p class="hmc-hint" id="hmc-email-msg" aria-live="polite">${live ? "" : "Prototype: on the live site this sends the plan through Klaviyo."}</p>`);
    const d = document.getElementById("hmc-dialog");
    const ok = d.querySelector("[data-dialog-ok]");
    const input = d.querySelector("#hmc-email");
    const msg = d.querySelector("#hmc-email-msg");
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); ok.click(); } });
    ok.addEventListener("click", async () => {
      const email = (input.value || "").trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { input.focus(); msg.textContent = "Enter a valid email address."; return; }
      const wantsOptIn = !!d.querySelector("#hmc-email-optin")?.checked;
      ok.disabled = true; ok.textContent = "Sending…";
      try {
        const res = live ? await sendToKlaviyo(email, wantsOptIn) : { event: true, optin: wantsOptIn || null };
        track("hmc_email", compute(), { hmc_optin: wantsOptIn, hmc_optin_ok: res.optin });
        msg.textContent = L("emailSent") + (res.optin === false ? " (We couldn't add you to the tips list, but your plan is on its way.)" : "");
        ok.textContent = "Sent";
        setTimeout(() => { d.close(); announce(L("emailSent"), { visible: true }); }, 1400);
      } catch (err) {
        ok.disabled = false; ok.textContent = "Send my plan";
        msg.textContent = L("emailError");
      }
    });
    setTimeout(() => input.focus(), 50);
  }

  // Klaviyo client APIs: public company id in the query string, no secrets on the page.
  // Returns { event: bool, optin: bool|null } so a failed opt-in never hides a sent plan.
  async function sendToKlaviyo(email, wantsOptIn) {
    const k = cfg.klaviyo;
    const snap = planSnapshot(compute());
    const headers = { "Content-Type": "application/json", revision: k.revision || "2024-10-15" };
    const base = "https://a.klaviyo.com/client";
    const ev = await fetch(`${base}/events/?company_id=${encodeURIComponent(k.companyId)}`, {
      method: "POST", headers, body: JSON.stringify({
        data: { type: "event", attributes: {
          properties: snap,
          value: snap.cans_total,
          metric: { data: { type: "metric", attributes: { name: k.metric || "Help Me Choose Plan Saved" } } },
          profile: { data: { type: "profile", attributes: { email, properties: { help_me_choose_plan_url: snap.plan_url, help_me_choose_people: snap.people, help_me_choose_days: snap.days } } } },
        } },
      }),
    });
    if (!ev.ok && ev.status !== 202) throw new Error("event " + ev.status);
    let optin = null;
    if (wantsOptIn && k.listId) {
      // The client subscription endpoint subscribes the address to the list with email
      // marketing consent implied (a consent block in the payload is rejected, verified
      // 2026-09-15). A double-opt-in list sends the confirmation email first.
      try {
        const sub = await fetch(`${base}/subscriptions/?company_id=${encodeURIComponent(k.companyId)}`, {
          method: "POST", headers, body: JSON.stringify({
            data: { type: "subscription", attributes: {
              custom_source: "Help Me Choose planner",
              profile: { data: { type: "profile", attributes: { email } } },
            }, relationships: { list: { data: { type: "list", id: k.listId } } } },
          }),
        });
        optin = sub.ok || sub.status === 202;
      } catch (e) { optin = false; }
    }
    return { event: true, optin };
  }

  async function copyLink() {
    writeHash();
    try {
      await navigator.clipboard.writeText(location.href);
      announce("Link copied. Anyone who opens it sees this exact plan.", { visible: true });
    } catch {
      announce("Copy this address from your browser bar to share the plan.", { visible: true });
    }
    track("hmc_copy", null);
  }

  function showDialog(title, body, html, extraClass) {
    let d = document.getElementById("hmc-dialog");
    if (!d) {
      d = document.createElement("dialog");
      d.id = "hmc-dialog";
      document.body.appendChild(d);
      d.addEventListener("click", (e) => {
        if (e.target !== d) return;
        const b = d.getBoundingClientRect();
        if (e.clientX < b.left || e.clientX > b.right || e.clientY < b.top || e.clientY > b.bottom) d.close();
      });
    }
    d.className = "hmc-dialog " + (extraClass || "");
    d.innerHTML = h`<form method="dialog" onsubmit="return false">${title ? h`<h2 class="hmc-h2">${esc(title)}</h2>` : ""}${body ? h`<p class="hmc-p">${esc(body)}</p>` : ""}${html || ""}<button type="button" class="hmc-btn hmc-btn-ghost hmc-dialog-close" aria-label="Close">${extraClass ? "×" : "Close"}</button></form>`;
    d.querySelector(".hmc-dialog-close").addEventListener("click", () => d.close());
    d.showModal();
  }

  /* Liquid emits strings and "true"/"false"; make the shapes the planner expects. */
  function normalizeCatalog(raw) {
    const b = (v) => v === true || v === "true";
    const n = (v) => { const x = parseFloat(String(v ?? "").replace(/,/g, "")); return isNaN(x) ? 0 : x; };
    const list = (v) => Array.isArray(v) ? v : String(v ?? "").split(",");
    const decode = (s) => String(s ?? "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&#39;|&rsquo;/g, "'").replace(/&quot;|&ldquo;|&rdquo;/g, '"').replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&#(\d+);/g, (_, n) => String.fromCharCode(n));
    const stripHtml = (s) => decode(String(s ?? "").replace(/<\/?(li|p|br|div|tr)[^>]*>/gi, "\n").replace(/<[^>]+>/g, " ")).split("\n").map((x) => x.replace(/\s+/g, " ").trim()).filter(Boolean);
    const cans = (raw.cans || []).map((c) => ({
      ...c,
      id: String(c.id), price: n(c.price), compareAt: c.compareAt == null ? null : n(c.compareAt),
      available: c.available == null ? true : b(c.available),
      servings: n(c.servings), cal: n(c.cal), protein: n(c.protein),
      allergens: list(c.allergens).map((a) => a.trim().toLowerCase()).filter(Boolean),
      gf: b(c.gf), df: b(c.df), sf: b(c.sf), vegan: b(c.vegan), veg: b(c.veg),
      shelf: c.shelf ? String(c.shelf) : null,
      waterAmt: c.waterAmt == null || c.waterAmt === "" ? null : n(c.waterAmt),
      waterUnit: Array.isArray(c.waterUnit) ? c.waterUnit[0] : (c.waterUnit || null),
      hot: b(c.hot), cold: b(c.cold), rte: b(c.rte),
      desc: c.desc ? decode(c.desc) : "",
      highlight: c.highlight ? decode(c.highlight) : "",
      tags: Array.isArray(c.tags) ? c.tags : list(c.tags).map((t) => t.trim()).filter(Boolean),
      rating: c.rating == null || c.rating === "" ? null : n(c.rating),
      ratingCount: c.ratingCount == null || c.ratingCount === "" ? 0 : n(c.ratingCount),
    })).filter((c) => c.servings > 0 && c.price > 0);
    const kits = (raw.kits || []).map((k) => {
      const m = /(\d+)[- ]Day/i.exec(k.title || "");
      const contents = Array.isArray(k.contents) ? k.contents : stripHtml(k.contentsHtml);
      return { ...k, id: String(k.id), price: n(k.price), servings: n(k.servings), calDay: n(k.calDay), protDay: n(k.protDay), days: k.days ? n(k.days) : (m ? parseInt(m[1], 10) : 0), contents, products: Array.isArray(k.products) ? k.products.filter(Boolean) : [] };
    }).filter((k) => k.days > 0 && k.calDay > 0);
    return { cans, kits };
  }

  /* ---------- public ---------- */
  function init(opts) {
    cfg = Object.assign({}, DEFAULTS, opts.config || {});
    cfg.labels = Object.assign({}, DEFAULTS.labels, (opts.config && opts.config.labels) || {});
    cfg.klaviyo = Object.assign({}, DEFAULTS.klaviyo, (opts.config && opts.config.klaviyo) || {});
    cfg.educationLinks = Object.assign({}, DEFAULTS.educationLinks, (opts.config && opts.config.educationLinks) || {});
    cfg.words = Object.assign({}, DEFAULTS.words, (opts.config && opts.config.words) || {});
    // Restrictions from JSON cannot carry test functions: merge labels onto the defaults by key.
    if (opts.config && Array.isArray(opts.config.restrictions)) {
      cfg.restrictions = opts.config.restrictions.map((o) => Object.assign({}, DEFAULTS.restrictions.find((d) => d.key === o.key) || {}, o)).filter((x) => typeof x.test === "function");
    }
    if (!Array.isArray(cfg.calories) || !cfg.calories.length) cfg.calories = DEFAULTS.calories;
    catalog = normalizeCatalog(opts.catalog);
    root = typeof opts.root === "string" ? document.querySelector(opts.root) : opts.root;
    state = readHash();
    root.addEventListener("click", onClick);
    document.addEventListener("pointerdown", () => { pointerDown = true; }, true);
    document.addEventListener("pointerup", () => { pointerDown = false; if (pendingRender) { const f = pendingRender; pendingRender = null; setTimeout(f, 0); } }, true);
    document.addEventListener("pointercancel", () => { pointerDown = false; if (pendingRender) { const f = pendingRender; pendingRender = null; setTimeout(f, 0); } }, true);
    window.addEventListener("pageshow", (e) => { if (e.persisted) render({ focus: false }); });
    document.addEventListener("click", (e) => { const d = document.getElementById("hmc-dialog"); if (d && d.contains(e.target) && e.target.closest("[data-act]")) onClick(e); });
    root.addEventListener("change", onInput);
    root.addEventListener("input", onInput);
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeTips(); });
    root.addEventListener("keydown", (e) => {
      const radio = e.target.closest && e.target.closest('[role="radio"]');
      if (!radio || !["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
      const radios = [...radio.parentElement.querySelectorAll('[role="radio"]')];
      let i = radios.indexOf(radio);
      i = e.key === "Home" ? 0 : e.key === "End" ? radios.length - 1 : (i + (/Right|Down/.test(e.key) ? 1 : -1) + radios.length) % radios.length;
      e.preventDefault();
      radios[i].click();
    });
    window.addEventListener("resize", applyStickyOffset, { passive: true });
    window.addEventListener("load", applyStickyOffset);
    let measured = 0;
    window.addEventListener("scroll", () => { if (measured++ < 3) applyStickyOffset(); }, { passive: true });
    window.addEventListener("hashchange", () => {
      if (!hashHasPlanKeys(location.hash)) return; // a skip link or "#" must not reset the plan
      const next = readHash();
      if (hashFor(next) !== hashFor(state)) { state = next; render({ focus: false }); }
    });
    render();
    return { compute, getState: () => state, kitQuantities: () => kitQuantities(compute().kit) };
  }

  return { init, compute: () => compute() };
})();
